import React, { useState, useEffect, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  BarChart3, 
  TrendingUp, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  FileText,
  Users,
  Wifi,
  Lock,
  Eye,
  Calendar,
  Download,
  RefreshCw,
  Target,
  Award,
  PieChart,
  Activity,
  Filter,
  ArrowUp,
  ArrowDown,
  Minus,
  TrendingDown,
  BarChart,
  LineChart,
  Settings,
  Info,
  Zap,
  Grid,
  Building2,
  X
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { supabase, executeWithAuthRetry, dbHelpers, backfillAssessmentIdFromAssessment } from '../lib/supabase'
import { logger } from '../utils/logger'
import { getUserTypeLabel as getTypeLabel } from '../utils/userTypeSync'
import { mergeAssessmentData, getAssessmentStats, cleanupOldAssessmentData } from '../utils/assessmentDataHandler'

import AISummary from '../components/AISummary'
import AgencyRiskAssignment from '../components/AgencyRiskAssignment'

const Dashboard = () => {
  const { user, profile } = useAuth()
  const location = useLocation()
  const [recentActivities, setRecentActivities] = useState([])
  const [upcomingTasks, setUpcomingTasks] = useState([])
  const [dashboardData, setDashboardData] = useState({
    score: 0,
    riskLevel: 'medium',
    lastAssessmentDate: null,
    assessmentHistory: [],
    recommendations: [],
    categories: {}
  })
  const [agencyRiskScores, setAgencyRiskScores] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedTimeframe, setSelectedTimeframe] = useState('3months')
  const [analyticsView, setAnalyticsView] = useState('overview')
  const [selectedMetric, setSelectedMetric] = useState('security_score')
  const [comparisonMode, setComparisonMode] = useState('industry')
  const [selectedAgency, setSelectedAgency] = useState(null)
  const [selectedAgencyForAssessments, setSelectedAgencyForAssessments] = useState(null)
  const [agencyAssessments, setAgencyAssessments] = useState([])
  const [showAssessmentModal, setShowAssessmentModal] = useState(false)
  // Assign Compliance modal state
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assignDates, setAssignDates] = useState({
    startDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  })
  const [selectedAssessmentForAssign, setSelectedAssessmentForAssign] = useState(null)
  const [assignAllCompleted, setAssignAllCompleted] = useState(false)
  const [assignHasInProgress, setAssignHasInProgress] = useState(false)
  const [assignHasPending, setAssignHasPending] = useState(false)
  const navigate = useNavigate()
  const [fullAssessmentForAssign, setFullAssessmentForAssign] = useState(null)
  const [assignPreviewTasks, setAssignPreviewTasks] = useState([])
  const [assignPreviewLoading, setAssignPreviewLoading] = useState(false)
  const [assignOwnerId, setAssignOwnerId] = useState(null)
  const [assignOwnerAgencyId, setAssignOwnerAgencyId] = useState(null)
  const [assignError, setAssignError] = useState(null)
  const [assignJustCreated, setAssignJustCreated] = useState(false)
  // Map of assessment.id -> array of failing 'No' responses with question details
  const [noResponseMap, setNoResponseMap] = useState({})

  // Removed scheduling toggle; button visibility depends solely on data state

  // Modal for updating a single non-compliant answer (No -> Yes)
  const [showResponseModal, setShowResponseModal] = useState(false)
  const [responseModalItem, setResponseModalItem] = useState(null)
  const [responseModalAssessment, setResponseModalAssessment] = useState(null)

  const openResponseModal = (assessment, item) => {
    try {
      setResponseModalAssessment(assessment)
      setResponseModalItem(item)
      setShowResponseModal(true)
    } catch (_) {
      setShowResponseModal(true)
    }
  }

  const closeResponseModal = () => {
    setShowResponseModal(false)
    setResponseModalItem(null)
    setResponseModalAssessment(null)
  }

  const markItemCompliant = async () => {
    try {
      const aid = responseModalAssessment?.id
      const qid = responseModalItem?.question_id
      if (!aid || !qid) {
        closeResponseModal()
        return
      }

      // Try to update an existing response; insert if missing
      const runFetch = async () => supabase
        .from('assessment_responses')
        .select('id')
        .eq('assessment_id', aid)
        .eq('question_id', qid)
        .limit(1)
      const { data: existing } = await executeWithAuthRetry(runFetch)

      if (existing && existing.length > 0) {
        const runUpdate = async () => supabase
          .from('assessment_responses')
          .update({ response_value: 'Yes' })
          .eq('id', existing[0].id)
        await executeWithAuthRetry(runUpdate)
      } else {
        const runInsert = async () => supabase
          .from('assessment_responses')
          .insert({ assessment_id: aid, question_id: qid, response_value: 'Yes' })
        await executeWithAuthRetry(runInsert)
      }

      // Remove from local non-compliant list
      setNoResponseMap(prev => {
        const list = Array.isArray(prev[aid]) ? prev[aid].filter(i => String(i.question_id) !== String(qid)) : []
        return { ...prev, [aid]: list }
      })

      closeResponseModal()
      try { await loadDashboardData() } catch (_) {}
    } catch (e) {
      console.warn('Failed to mark item compliant:', e)
      closeResponseModal()
    }
  }

  // Helper: normalize compliance titles to match legacy and new formats
  const normalizeComplianceTitle = (t) => String(t || '')
    .replace(/\[AG:[^\]]+\]\s*/gi, '')
    .replace(/\[AID:[^\]]+\]\s*/gi, '')
    .replace(/\[Compliance\]\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

  // Extract embedded AID marker like [AID:...]
  const extractAID = (s) => {
    const m = String(s || '').match(/\[AID:([^\]]+)\]/i)
    return m ? m[1].trim() : null
  }

  // Fuzzy matching helpers: tokenization with stop-words and simple synonyms, Jaccard similarity
  const stopWords = new Set(['the','to','and','for','of','in','on','with','overall','provide','regular','address','this','ensure','concern','can','be','is','are','an','a'])
  const synonymMap = {
    awareness: 'training',
    cybersecurity: 'security',
    controls: 'control',
    passwords: 'password',
    trainings: 'training'
  }
  const tokenizeTitle = (s) => {
    const base = normalizeComplianceTitle(s)
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w && w.length >= 3 && !stopWords.has(w))
      .map(w => synonymMap[w] || w)
    const tokens = new Set(base)
    // Add combined tokens for common compliance phrases
    const has = (t) => tokens.has(t)
    if (has('network') && has('security')) tokens.add('network_security')
    if (has('data') && has('protection')) tokens.add('data_protection')
    if (has('access') && has('control')) tokens.add('access_control')
    if (has('security') && has('training')) tokens.add('security_training')
    return Array.from(tokens)
  }

  const jaccardSimilarity = (a, b) => {
    const sa = new Set(tokenizeTitle(a))
    const sb = new Set(tokenizeTitle(b))
    if (!sa.size || !sb.size) return 0
    let inter = 0
    sa.forEach(w => { if (sb.has(w)) inter++ })
    const union = sa.size + sb.size - inter
    return union > 0 ? inter / union : 0
  }

  const fuzzyStatusFromCandidates = (text, rows, threshold = 0.7, item = null) => {
    let bestScore = 0
    let bestStatus = undefined
    const input = String(text || '')
    // Precompute title tokens from input for loose overlap boost
    const inputTokens = new Set(tokenizeTitle(input))
    ;(rows || []).forEach(row => {
      const candTitle = String(row?.title || '')
      const candDesc = String(row?.description || '')
      const titleScore = jaccardSimilarity(text, candTitle)
      const descScore = jaccardSimilarity(text, candDesc)
      let score = 0.7 * titleScore + 0.3 * descScore
      // Boost when many input tokens appear in candidate
      let overlap = 0
      const candTokens = new Set(tokenizeTitle(`${candTitle} ${candDesc}`))
      inputTokens.forEach(t => { if (candTokens.has(t)) overlap++ })
      if (overlap >= 3) score = Math.max(score, 0.6)
      // Bonus when question_id matches
      if (item?.question_id && row?.question_id && String(item.question_id) === String(row.question_id)) {
        score = Math.min(1, score + 0.2)
      }
      if (score > bestScore) {
        bestScore = score
        bestStatus = normalizeStatus(row?.status) || 'pending'
      }
    })
    return bestScore >= threshold ? bestStatus : undefined
  }

  // Load "No" responses for a given assessment and map to question details
  const loadNoResponsesForAssessment = async (assessment) => {
    try {
      const aid = assessment?.id
      if (!aid) return

      // Avoid reloading if already present
      if (noResponseMap[aid]) return

      // First, check embedded responses on the assessment object
      const embedded = assessment?.responses || {}
      let failingIds = []
      if (embedded && Object.keys(embedded).length > 0) {
        failingIds = Object.entries(embedded)
          .filter(([qid, value]) => {
            const v = typeof value === 'object' ? (value?.score ?? value?.value) : value
            return (typeof v === 'string' && v.toLowerCase() === 'no') || v === false || (typeof v === 'number' && v < 0.5)
          })
          .map(([qid]) => String(qid))
      }

      // If none found (or embedded missing), fetch from assessment_responses with explicit "No" values
      if (failingIds.length === 0) {
        const runRes = async () => supabase
          .from('assessment_responses')
          .select('question_id, response_value')
          .eq('assessment_id', aid)
          .in('response_value', ['No','no','NO'])
        const { data: rows, error: resError } = await executeWithAuthRetry(runRes)
        if (resError) {
          console.warn('Failed to load assessment_responses for assessment', aid, resError?.message || resError)
        }
        failingIds = (rows || []).map(r => String(r.question_id))
      }

      // If still none, set empty and return
      if (failingIds.length === 0) {
        setNoResponseMap(prev => ({ ...prev, [aid]: [] }))
        return
      }

      // Fetch question titles/categories for failing IDs
      const runQ = async () => supabase
        .from('assessment_questions')
        .select('id, question_text, assessment_categories(name)')
        .in('id', failingIds)
      const { data: questions, error: qError } = await executeWithAuthRetry(runQ)
      if (qError) {
        console.warn('Failed to load assessment_questions for assessment', aid, qError?.message || qError)
      }
      const qMap = {}
      ;(questions || []).forEach(q => {
        qMap[String(q.id)] = { title: q.question_text, category: q?.assessment_categories?.name || null }
      })

      const items = failingIds.map(qid => ({
        question_id: qid,
        question_text: qMap[qid]?.title || `Question ${qid}`,
        category: qMap[qid]?.category || null,
        response_value: 'No'
      }))

      setNoResponseMap(prev => ({ ...prev, [aid]: items }))
    } catch (e) {
      console.warn('Failed to load "No" responses for assessment', assessment?.id, e)
      setNoResponseMap(prev => ({ ...prev, [assessment?.id]: null }))
    }
  }

  // Prefetch "No" responses when agency risk scores change
  useEffect(() => {
    const doLoad = async () => {
      try {
        const list = agencyRiskScores || []
        for (const a of list) {
          await loadNoResponsesForAssessment(a)
        }
      } catch (e) {
        console.warn('Prefetch no responses failed:', e)
      }
    }
    if (agencyRiskScores && agencyRiskScores.length > 0) {
      doLoad()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyRiskScores])

  // Helper: normalize status values across UI and DB variants
  const normalizeStatus = (s) => {
    const v = String(s || '').trim().toLowerCase()
    if (!v) return undefined
    if (['in_progress','in progress','in-progress','progress','ongoing'].includes(v)) return 'in_progress'
    if (['completed','complete','done','finished'].includes(v)) return 'completed'
    if (['scheduled','schedule','planned','plan','queued'].includes(v)) return 'scheduled'
    if (['pending','todo','not started','new'].includes(v)) return 'pending'
    if (['overdue','late','expired','past due'].includes(v)) return 'overdue'
    return v
  }

  // Helper: merge statuses with precedence so stronger states aren't overridden by 'pending'
  const statusRank = (s) => {
    const v = normalizeStatus(s)
    if (v === 'completed') return 4
    if (v === 'in_progress') return 3
    if (v === 'scheduled') return 2
    if (v === 'overdue') return 2
    if (v === 'pending') return 1
    return 0
  }
  const mergeStatus = (prev, next) => {
    const a = normalizeStatus(prev)
    const b = normalizeStatus(next)
    if (!a) return b || undefined
    if (!b) return a || undefined
    return statusRank(b) >= statusRank(a) ? b : a
  }

  // Render a status badge mapped from tasks.status
  const renderStatusBadge = (status) => {
    const s = normalizeStatus(status)
    if (!s) return null
    const label = s === 'completed' ? 'Completed' 
      : s === 'in_progress' ? 'In Progress' 
      : s === 'scheduled' ? 'Scheduled'
      : s === 'overdue' ? 'Overdue'
      : 'Pending'
    const cls = s === 'completed'
      ? 'text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200'
      : s === 'in_progress'
      ? 'text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200'
      : s === 'scheduled'
      ? 'text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200'
      : s === 'overdue'
      ? 'text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200'
      : 'text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200'
    return (<span className={cls}>{label}</span>)
  }


  // Load dashboard data from Supabase
  useEffect(() => {
    if (user) {
      loadDashboardData()
      loadUpcomingTasks()
      // Fetch agency risk scores for admin users
      console.log('User Profile Debug:', {
        user_type: profile?.user_type,
        role: profile?.role,
        isAdmin: profile?.user_type === 'it_administrator' || 
                 profile?.user_type === 'security_officer' ||
                 profile?.role === 'Administrator'
      })
      if (profile?.user_type === 'it_administrator' || 
          profile?.user_type === 'security_officer' ||
          profile?.role === 'Administrator') {
        console.log('Fetching agency risk scores for admin user...')
        fetchAgencyRiskScores()
      }
    }
    loadActivities()

    // Clean up old assessment data from localStorage
    cleanupOldAssessmentData()

    // Listen for storage events to refresh tasks when they're created/updated
    const handleStorageChange = (e) => {
      if (e.key === 'recentActivities') {
        // Reload activities when they change
        loadActivities()
        // Also reload tasks since activities are created when tasks are created/updated
        if (user) {
          loadUpcomingTasks()
          // Refresh agency compliance status to reflect any newly completed tasks
          if (profile?.user_type === 'it_administrator' || 
              profile?.user_type === 'security_officer' || 
              profile?.role === 'Administrator') {
            fetchAgencyRiskScores()
          }
          // Refresh modal task statuses if open
          if (showAssignModal) {
            refreshAssignModalStatuses()
          }
        }
      }
    }

    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange)

    // Custom event listener for same-tab updates (since storage event doesn't fire in same tab)
    const handleTaskUpdate = () => {
      if (user) {
        loadUpcomingTasks()
        // Refresh agency compliance status in same-tab updates
        if (profile?.user_type === 'it_administrator' || 
            profile?.user_type === 'security_officer' || 
            profile?.role === 'Administrator') {
          fetchAgencyRiskScores()
        }
        // Refresh modal task statuses if open
        if (showAssignModal) {
          refreshAssignModalStatuses()
        }
      }
      loadActivities()
    }

    window.addEventListener('taskUpdated', handleTaskUpdate)

    // Cleanup event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('taskUpdated', handleTaskUpdate)
    }
  }, [user])

  // Enhanced refresh mechanisms for better score updates
  useEffect(() => {
    if (!user) return

    // Handle window focus (user returns from assessment)
    const handleFocus = () => {
      console.log('Window gained focus, refreshing dashboard...')
      loadDashboardData()
    }

    // Handle visibility change (more reliable than focus)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, refreshing dashboard...')
        setTimeout(() => loadDashboardData(), 500) // Small delay to ensure data is saved
      }
    }

    // Handle storage events for cross-tab communication
    const handleStorageChange = (e) => {
      if (e.key === 'assessment_completed' || e.key === 'dashboard_refresh_needed') {
        console.log('Assessment completed in another tab, refreshing...')
        setTimeout(() => loadDashboardData(), 1000) // Delay to ensure data is saved
      }
    }

    // Handle custom events for same-tab communication
    const handleAssessmentComplete = (e) => {
      console.log('Assessment completed event received, refreshing dashboard...')
      setTimeout(() => loadDashboardData(), 1000)
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('assessmentCompleted', handleAssessmentComplete)

    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('assessmentCompleted', handleAssessmentComplete)
    }
  }, [user])

  // Refresh data when navigating to dashboard
  useEffect(() => {
    if (user && location.pathname === '/dashboard') {
      loadDashboardData()
    }
  }, [location.pathname, user])

  // Manual refresh function
  const handleRefresh = () => {
    if (user) {
      loadDashboardData()
      loadUpcomingTasks()
    }
    loadActivities()
  }

  const handleAgencySelect = (agency) => {
    setSelectedAgency(agency)
  }

  const handleViewAssessments = async (agency) => {
    try {
      const agencyName = typeof agency === 'string' ? agency : agency?.agency
      setSelectedAgencyForAssessments(agencyName || '')
      setLoading(true)
      
      // Fetch assessments for users in this agency
      const runUsers = async () => supabase
        .from('users')
        .select('id, name, email')
        .eq('agency', agencyName)
      const { data: agencyUsers } = await executeWithAuthRetry(runUsers)
      
      if (agencyUsers && agencyUsers.length > 0) {
        const userIds = agencyUsers.map(user => user.id)
        
        // Fetch assessments for these users
        const runAgencyAssess = async () => supabase
          .from('assessments')
          .select(`
            *,
            users!inner(name, email, agency)
          `)
          .in('user_id', userIds)
          .eq('status', 'completed')
          .order('completed_at', { ascending: false })
        const { data: assessments, error } = await executeWithAuthRetry(runAgencyAssess)
        
        if (error) throw error
        
        // Enhance assessments with localStorage data if available
        const enhancedAssessments = mergeAssessmentData(assessments || [])
        setAgencyAssessments(enhancedAssessments)
      } else {
        setAgencyAssessments([])
      }
      
      setShowAssessmentModal(true)
    } catch (error) {
      console.error('Error loading agency assessments:', error)
      setAgencyAssessments([])
      setShowAssessmentModal(true)
    } finally {
      setLoading(false)
    }
  }

  const handleRiskAssigned = () => {
    // Refresh agency risk scores when a new risk is assigned
    fetchAgencyRiskScores()
  }

  // Open modal to assign compliance with dates
  const openAssignModal = (assessment) => {
    setSelectedAssessmentForAssign(assessment)
    setAssignDates({
      startDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    })
    setAssignJustCreated(false)
    setShowAssignModal(true)
    // Load full assessment and derive preview items
    ;(async () => {
      try {
        setAssignPreviewLoading(true)
        const run = async () => supabase
          .from('assessments')
          .select('*')
          .eq('id', assessment.id)
          .single()
        const { data: fullAssessment, error } = await executeWithAuthRetry(run)
        if (error) throw error
        const base = fullAssessment || assessment
        // Build question mappings to connect responses to friendly titles and categories
        let questionTitles = {}
        let questionMap = {}
        try {
          const runQuestions = async () => supabase
            .from('assessment_questions')
            .select(`
              id, question_text,
              assessment_categories(name)
            `)
            .eq('is_active', true)
          const { data: questionsRows } = await executeWithAuthRetry(runQuestions)
          ;(questionsRows || []).forEach(q => {
            const catName = q?.assessment_categories?.name || 'General'
            questionTitles[q.id] = q.question_text
            questionMap[q.id] = { title: q.question_text, category: catName }
          })
        } catch (e) {
          console.warn('Failed to fetch assessment questions for compliance mapping:', e)
        }
        // Fallback: if responses are missing, read explicit 'no' responses from assessment_responses
        let responsesObj = base?.responses || {}
        try {
          if (!responsesObj || Object.keys(responsesObj).length === 0) {
            const runResponses = async () => supabase
              .from('assessment_responses')
              .select('question_id, response_value')
              .eq('assessment_id', base.id)
            const { data: respRows } = await executeWithAuthRetry(runResponses)
            const map = {}
            ;(respRows || []).forEach(r => {
              const val = String(r?.response_value || '').toLowerCase()
              if (val === 'no') map[r.question_id] = 'no'
            })
            responsesObj = map
          }
        } catch (e) {
          console.warn('Failed to fetch assessment_responses for compliance mapping:', e)
        }
        const baseWithQuestions = { ...base, question_titles: questionTitles, questions: questionMap, responses: responsesObj }
        setFullAssessmentForAssign(baseWithQuestions)
        const ownerId = baseWithQuestions?.user_id || baseWithQuestions?.userId || null
        setAssignOwnerId(ownerId)
        // Fetch owner's agency_id for proper scoping of tasks (use local var for immediate queries)
        let ownerAgencyId = null
        try {
          const { data: ownerProfile } = await supabase
            .from('users')
            .select('id, agency_id')
            .eq('id', ownerId)
            .single()
          ownerAgencyId = ownerProfile?.agency_id || null
          setAssignOwnerAgencyId(ownerAgencyId)
        } catch (e) {
          console.warn('Failed to fetch owner agency_id:', e)
          ownerAgencyId = null
          setAssignOwnerAgencyId(null)
        }
        const preview = dbHelpers.generateComplianceTasksFromAssessment(baseWithQuestions) || []
        // Determine completion state from existing tasks
        let merged = preview
        setAssignAllCompleted(false)
        setAssignHasInProgress(false)
        setAssignHasPending(false)
        if (ownerId && preview.length > 0) {
          try {
            // Fetch all compliance tasks for this assessment and relevant assignees
            const runTasks = async () => supabase
              .from('tasks')
              .select('id,title,description,status,assigned_to,user_id,agency_id,assessment_id,question_id')
              // Fetch all tasks for this assessment and assignees; filter later by normalized title
              .eq('assessment_id', base?.id)
              .or(`assigned_to.eq.${ownerId},user_id.eq.${ownerId}${user?.id && user.id !== ownerId ? `,assigned_to.eq.${user.id},user_id.eq.${user.id}` : ''}${ownerAgencyId ? `,agency_id.eq.${ownerAgencyId}` : ''}`)
            const { data: existing, error: tErr } = await executeWithAuthRetry(runTasks)
            if (!tErr && Array.isArray(existing)) {
              const previewTitleSet = new Set((preview || []).map(t => normalizeComplianceTitle(t.title)))
              const previewDescSet = new Set((preview || []).map(t => normalizeComplianceTitle(t.description)))
              const statusByKey = {}
              const statusByAny = {}
              const statusByOwnerTitle = {}
              const statusByQid = {}
              const statusByQidAny = {}
              const statusByDescKey = {}
              const statusByDescAny = {}
              ;(existing || []).forEach(row => {
                const titleNorm = normalizeComplianceTitle(row.title)
                const descNorm = normalizeComplianceTitle(row.description)
                const a = row.assessment_id || ''
                const ag = row.agency_id || ''
                const s = normalizeStatus(row.status) || 'pending'
                // Map by question_id when present
                if (row.question_id) {
                  const qidStr = String(row.question_id)
                  const prevQExact = statusByQid[`${a}|${qidStr}`]
                  statusByQid[`${a}|${qidStr}`] = mergeStatus(prevQExact, s)
                  const prevQAny = statusByQidAny[qidStr]
                  statusByQidAny[qidStr] = mergeStatus(prevQAny, s)
                }
                // Title/Description-based maps for backwards compatibility
                if (previewTitleSet.has(titleNorm) || (descNorm && previewDescSet.has(descNorm))) {
                  const keyExact = `${a}|${ag}|${titleNorm}`
                  const prevExact = statusByKey[keyExact]
                  statusByKey[keyExact] = mergeStatus(prevExact, s)
                  // Index legacy null-agency variant to catch older tasks
                  const keyLegacy = `${a}||${titleNorm}`
                  const prevLegacy = statusByKey[keyLegacy]
                  statusByKey[keyLegacy] = mergeStatus(prevLegacy, s)
                  // Agency-agnostic fallback (assessment + title)
                  const keyAny = `${a}|||${titleNorm}`
                  const prevAny = statusByAny[keyAny]
                  statusByAny[keyAny] = mergeStatus(prevAny, s)
                  // Description keys when available
                  if (descNorm) {
                    const dExact = `${a}|${ag}|${descNorm}`
                    const prevDExact = statusByDescKey[dExact]
                    statusByDescKey[dExact] = mergeStatus(prevDExact, s)
                    const dLegacy = `${a}||${descNorm}`
                    const prevDLegacy = statusByDescKey[dLegacy]
                    statusByDescKey[dLegacy] = mergeStatus(prevDLegacy, s)
                    const dAny = `${a}|||${descNorm}`
                    const prevDAny = statusByDescAny[dAny]
                    statusByDescAny[dAny] = mergeStatus(prevDAny, s)
                  }
                  // Owner/title+desc fallback across assessments
                  const isOwnerRow = row.assigned_to === ownerId || row.user_id === ownerId
                  if (isOwnerRow) {
                    const prevOwnerT = statusByOwnerTitle[titleNorm]
                    statusByOwnerTitle[titleNorm] = mergeStatus(prevOwnerT, s)
                    if (descNorm) {
                      const prevOwnerD = statusByOwnerTitle[descNorm]
                      statusByOwnerTitle[descNorm] = mergeStatus(prevOwnerD, s)
                    }
                  }
                }
              })
              // Also search owner's tasks across assessments for matching titles
              let anyRows = []
              try {
                const runTasksAny = async () => supabase
                  .from('tasks')
                  .select('id,title,description,status,assigned_to,user_id,agency_id,assessment_id,question_id')
                  .or(`assigned_to.eq.${ownerId},user_id.eq.${ownerId}${user?.id && user.id !== ownerId ? `,assigned_to.eq.${user.id},user_id.eq.${user.id}` : ''}${ownerAgencyId ? `,agency_id.eq.${ownerAgencyId}` : ''}`)
                const { data: anyRowsData } = await executeWithAuthRetry(runTasksAny)
                anyRows = anyRowsData || []
                ;(anyRows || []).forEach(row => {
                  const titleNorm = normalizeComplianceTitle(row.title)
                  const descNorm = normalizeComplianceTitle(row.description)
                  if (!previewTitleSet.has(titleNorm) && !(descNorm && previewDescSet.has(descNorm))) return
                  const s = normalizeStatus(row.status)
                  // Map by question_id across assessments when present
                  if (row.question_id) {
                    const qidStr = String(row.question_id)
                    const prevQAny = statusByQidAny[qidStr]
                    statusByQidAny[qidStr] = mergeStatus(prevQAny, s)
                  }
                  const prevOwnerT = statusByOwnerTitle[titleNorm]
                  statusByOwnerTitle[titleNorm] = mergeStatus(prevOwnerT, s)
                  if (descNorm) {
                    const prevOwnerD = statusByOwnerTitle[descNorm]
                    statusByOwnerTitle[descNorm] = mergeStatus(prevOwnerD, s)
                  }
                })
              } catch {}
              // Prefer question_id match first; then exact agency/title and fallbacks; finally fuzzy by title tokens.
              const candidatesAll = [...(existing || []), ...((anyRows || []))]
              merged = preview.map(item => {
                const titleNorm = normalizeComplianceTitle(item.title)
                const descNormItem = normalizeComplianceTitle(item.description)
                const keyExact = `${base?.id || ''}|${ownerAgencyId || ''}|${titleNorm}`
                const keyLegacy = `${base?.id || ''}||${titleNorm}`
                const keyAny = `${base?.id || ''}|||${titleNorm}`
                const dExact = descNormItem ? `${base?.id || ''}|${ownerAgencyId || ''}|${descNormItem}` : null
                const dLegacy = descNormItem ? `${base?.id || ''}||${descNormItem}` : null
                const dAny = descNormItem ? `${base?.id || ''}|||${descNormItem}` : null
                const qKey = item.question_id ? `${base?.id || ''}|${String(item.question_id)}` : null
                let status = (qKey ? statusByQid[qKey] : undefined)
                  || (item.question_id ? statusByQidAny[String(item.question_id)] : undefined)
                  || statusByKey[keyExact] || statusByKey[keyLegacy] || statusByAny[keyAny]
                  || (dExact ? statusByDescKey[dExact] : undefined) || (dLegacy ? statusByDescKey[dLegacy] : undefined) || (dAny ? statusByDescAny[dAny] : undefined)
                  || statusByOwnerTitle[titleNorm] || (descNormItem ? statusByOwnerTitle[descNormItem] : undefined)
                if (!status) {
                  const fuzzy = fuzzyStatusFromCandidates(`${item.title} ${item.description || ''}`, candidatesAll, 0.5)
                  if (fuzzy) status = fuzzy
                }
                return status ? { ...item, status } : item
              })
              setAssignAllCompleted(merged.length > 0 && merged.every(i => (i.status || '').toLowerCase() === 'completed'))
              setAssignHasInProgress(merged.some(i => (i.status || '').toLowerCase() === 'in_progress'))
              setAssignHasPending(merged.some(i => (i.status || '').toLowerCase() === 'pending'))

              // Augment preview with existing compliance tasks not part of generated recommendations
              try {
                const titlesSet = new Set((preview || []).map(t => t.title))
                const orAssignees = `assigned_to.eq.${ownerId},user_id.eq.${ownerId}${user?.id && user.id !== ownerId ? `,assigned_to.eq.${user.id},user_id.eq.${user.id}` : ''}${assignOwnerAgencyId ? `,agency_id.eq.${assignOwnerAgencyId}` : ''}`
                const runExtraCurrent = async () => supabase
                  .from('tasks')
                  .select('id,title,description,category,priority,status,assessment_id,agency_id')
                  .ilike('title', '%[Compliance]%')
                  .eq('assessment_id', base?.id)
                  .or(orAssignees)
                const { data: extraCurrent } = await executeWithAuthRetry(runExtraCurrent)
                // Legacy extras disabled to avoid cross-agency contamination
                const extras = (extraCurrent || [])
                const toAdd = []
                for (const t of extras) {
                  if (t && t.title && !titlesSet.has(t.title)) {
                    titlesSet.add(t.title)
                    toAdd.push({
                      title: t.title,
                      description: t.description || 'Implement the recommended control to address identified gaps.',
                      category: t.category || undefined,
                      priority: (t.priority || 'high').toLowerCase(),
                      status: t.status ? normalizeStatus(t.status) : undefined,
                      assignee: 'owner',
                      include: false
                    })
                  }
                }
                if (toAdd.length > 0) {
                  merged = [...merged, ...toAdd]
                  setAssignAllCompleted(merged.length > 0 && merged.every(i => (i.status || '').toLowerCase() === 'completed'))
                  setAssignHasInProgress(merged.some(i => (i.status || '').toLowerCase() === 'in_progress'))
                  setAssignHasPending(merged.some(i => (i.status || '').toLowerCase() === 'pending'))
                }
              } catch (mergeErr) {
                console.warn('Failed to merge extra compliance tasks into preview:', mergeErr)
              }
            }
          } catch (e) {
            console.warn('Failed to check existing tasks for completion:', e)
          }
        }
        // Dedupe items by normalized title, grouping question_ids and merging status
        const dedupeAssignItems = (items) => {
          const byTitle = new Map()
          const prioRank = { critical: 4, high: 3, medium: 2, low: 1 }
          for (const it of (items || [])) {
            const key = normalizeComplianceTitle(it.title)
            const existing = byTitle.get(key)
            const mergedStatus = mergeStatus(existing?.status, it?.status)
            const qids = new Set([
              ...(existing?.question_ids || []),
              ...(existing?.question_id != null ? [String(existing.question_id)] : []),
              ...(it?.question_ids || []),
              ...(it?.question_id != null ? [String(it.question_id)] : [])
            ].filter(Boolean))
            const bestDesc = (existing?.description || '').length >= (it?.description || '').length ? (existing?.description || '') : (it?.description || '')
            const bestCat = existing?.category ?? it?.category
            const eP = String(existing?.priority || '').toLowerCase()
            const iP = String(it?.priority || '').toLowerCase()
            const bestPrio = (prioRank[eP] || 0) >= (prioRank[iP] || 0) ? existing?.priority : it?.priority
            byTitle.set(key, {
              ...it,
              title: it.title,
              description: bestDesc,
              category: bestCat,
              priority: bestPrio || it.priority,
              status: mergedStatus || (normalizeStatus(it.status) || undefined),
              question_ids: Array.from(qids),
              // keep one representative question_id for legacy paths
              question_id: Array.from(qids)[0] || it.question_id || existing?.question_id || null
            })
          }
          return Array.from(byTitle.values())
        }

        const deduped = dedupeAssignItems(merged)
        // Default each item to assign to the assessment owner and include in assignment; allow override per item in UI
        setAssignPreviewTasks(deduped.map(t => ({ ...t, assignee: 'owner', include: t.include === false ? false : true })))
        console.log('[AssignOpen] merged with status:', merged.filter(i => i.status).length, '/', merged.length)
        setTimeout(() => { try { refreshAssignModalStatuses() } catch {} }, 50)
      } catch (err) {
        console.warn('Failed to load assessment for compliance preview:', err)
        setAssignPreviewTasks([])
      } finally {
        setAssignPreviewLoading(false)
      }
    })()
  }

  const closeAssignModal = () => {
    setShowAssignModal(false)
    setSelectedAssessmentForAssign(null)
    setFullAssessmentForAssign(null)
    setAssignPreviewTasks([])
    setAssignPreviewLoading(false)
    setAssignHasInProgress(false)
    setAssignHasPending(false)
    setAssignError(null)
    setAssignJustCreated(false)
  }

  // Refresh modal task statuses dynamically when external updates happen
  const refreshAssignModalStatuses = async () => {
    try {
      if (!showAssignModal) return
      const ownerId = assignOwnerId || selectedAssessmentForAssign?.user_id || selectedAssessmentForAssign?.userId || null
      const assessmentId = fullAssessmentForAssign?.id || selectedAssessmentForAssign?.id || null
      const titles = (assignPreviewTasks || []).map(t => t.title)
      if (!ownerId || titles.length === 0) return
      // Resolve owner agency_id locally for immediate query scoping
      let ownerAgencyId = assignOwnerAgencyId || null
      if (!ownerAgencyId) {
        try {
          const { data: ownerProfile } = await supabase
            .from('users')
            .select('id, agency_id')
            .eq('id', ownerId)
            .single()
          ownerAgencyId = ownerProfile?.agency_id || null
          setAssignOwnerAgencyId(ownerAgencyId)
        } catch (e) {
          console.warn('Failed to fetch owner agency_id in refresh:', e)
        }
      }
      let existing = []
      let tErr = null
      const runTasks = async (includeQid = true) => {
        let orCond = `assigned_to.eq.${ownerId},user_id.eq.${ownerId}`
        if (user?.id && user.id !== ownerId) {
          orCond += `,assigned_to.eq.${user.id},user_id.eq.${user.id}`
        }
        if (ownerAgencyId) {
          orCond += `,agency_id.eq.${ownerAgencyId}`
        }
        const selectFields = includeQid
          ? 'id,title,description,status,assigned_to,user_id,agency_id,assessment_id,question_id'
          : 'id,title,description,status,assigned_to,user_id,agency_id,assessment_id'
        return supabase
          .from('tasks')
          .select(selectFields)
          // Fetch all tasks for this assessment and assignees; filter later by normalized title
          .eq('assessment_id', assessmentId)
          .or(orCond)
      }
      {
        const { data, error } = await executeWithAuthRetry(() => runTasks(true))
        if (!error) {
          existing = data || []
        } else {
          const msg = String(error?.message || '').toLowerCase()
          const qidMissing = msg.includes("could not find the 'question_id' column") || msg.includes('column question_id does not exist') || (msg.includes('could not find') && msg.includes('question_id') && msg.includes('schema cache'))
          if (qidMissing) {
            const { data: data2, error: err2 } = await executeWithAuthRetry(() => runTasks(false))
            if (!err2) {
              existing = data2 || []
            } else {
              tErr = err2
            }
          } else {
            tErr = error
          }
        }
      }
      if (tErr) { console.warn('Assign refresh tasks error:', tErr); return }
      console.log('[AssignRefresh] existing:', (existing||[]).length, 'ownerId:', ownerId, 'assessmentId:', assessmentId, 'ownerAgencyId:', ownerAgencyId)
      const previewTitleSet = new Set((assignPreviewTasks || []).map(t => normalizeComplianceTitle(t.title)))
      const previewDescSet = new Set((assignPreviewTasks || []).map(t => normalizeComplianceTitle(t.description)))
      const statusByKey = {}
      const statusByAny = {}
      const statusByOwnerTitle = {}
      const statusByQid = {}
      const statusByQidAny = {}
      const statusByDescKey = {}
      const statusByDescAny = {}
      ;(existing || []).forEach(row => {
        const titleNorm = normalizeComplianceTitle(row.title)
        const descNorm = normalizeComplianceTitle(row.description)
        const a = row.assessment_id || ''
        const ag = row.agency_id || ''
        const s = normalizeStatus(row.status) || 'pending'
        // Map by question_id when present (exact assessment match and assessment-agnostic fallback)
        if (row.question_id) {
          const qidStr = String(row.question_id)
          const qExactKey = `${a}|${qidStr}`
          const prevQExact = statusByQid[qExactKey]
          statusByQid[qExactKey] = mergeStatus(prevQExact, s)
          const prevQAny = statusByQidAny[qidStr]
          statusByQidAny[qidStr] = mergeStatus(prevQAny, s)
        }
        
        // Title/Description-based maps for backwards compatibility
        if (previewTitleSet.has(titleNorm) || (descNorm && previewDescSet.has(descNorm))) {
          const keyExact = `${a}|${ag}|${titleNorm}`
          const prevExact = statusByKey[keyExact]
          statusByKey[keyExact] = mergeStatus(prevExact, s)

          const keyAny = titleNorm
          const prevAny = statusByAny[keyAny]
          statusByAny[keyAny] = mergeStatus(prevAny, s)
          // Also index legacy null-agency variant for older tasks
          const keyLegacy = `${a}||${titleNorm}`
          const prevLegacy = statusByKey[keyLegacy]
          statusByKey[keyLegacy] = mergeStatus(prevLegacy, s)
          // Agency-agnostic fallback (assessment + title)
          const keyAny2 = `${a}|||${titleNorm}`
          const prevAny2 = statusByAny[keyAny2]
          statusByAny[keyAny2] = mergeStatus(prevAny2, s)
          // Description keys when available
          if (descNorm) {
            const dExact = `${a}|${ag}|${descNorm}`
            const prevDExact = statusByDescKey[dExact]
            statusByDescKey[dExact] = mergeStatus(prevDExact, s)
            const dLegacy = `${a}||${descNorm}`
            const prevDLegacy = statusByDescKey[dLegacy]
            statusByDescKey[dLegacy] = mergeStatus(prevDLegacy, s)
            const dAny = `${a}|||${descNorm}`
            const prevDAny = statusByDescAny[dAny]
            statusByDescAny[dAny] = mergeStatus(prevDAny, s)
          }
          // Owner/title+desc fallback across assessments
          const isOwnerRow = row.assigned_to === ownerId || row.user_id === ownerId
          if (isOwnerRow) {
            const prevOwnerT = statusByOwnerTitle[titleNorm]
            statusByOwnerTitle[titleNorm] = mergeStatus(prevOwnerT, s)
            if (descNorm) {
              const prevOwnerD = statusByOwnerTitle[descNorm]
              statusByOwnerTitle[descNorm] = mergeStatus(prevOwnerD, s)
            }
          }
        }
      })
      // Also search owner's tasks across assessments for matching titles and question_id
      let anyRows = []
      try {
        const runTasksAny = async (includeQid = true) => supabase
          .from('tasks')
          .select(includeQid ? 'id,title,description,status,assigned_to,user_id,agency_id,assessment_id,question_id' : 'id,title,description,status,assigned_to,user_id,agency_id,assessment_id')
          .or(`assigned_to.eq.${ownerId},user_id.eq.${ownerId}${user?.id && user.id !== ownerId ? `,assigned_to.eq.${user.id},user_id.eq.${user.id}` : ''}${ownerAgencyId ? `,agency_id.eq.${ownerAgencyId}` : ''}`)
        {
          const { data, error } = await executeWithAuthRetry(() => runTasksAny(true))
          if (!error) {
            anyRows = data || []
          } else {
            const msg = String(error?.message || '').toLowerCase()
            const qidMissing = msg.includes("could not find the 'question_id' column") || msg.includes('column question_id does not exist') || (msg.includes('could not find') && msg.includes('question_id') && msg.includes('schema cache'))
            if (qidMissing) {
              const { data: data2, error: err2 } = await executeWithAuthRetry(() => runTasksAny(false))
              if (!err2) {
                anyRows = data2 || []
              } else {
                throw err2
              }
            } else {
              throw error
            }
          }
        }
        console.log('[AssignRefresh] anyRows:', (anyRows||[]).length)
        ;(anyRows || []).forEach(row => {
          const titleNorm = normalizeComplianceTitle(row.title)
          const descNorm = normalizeComplianceTitle(row.description)
          const s = normalizeStatus(row.status) || 'pending'
          if (row.question_id) {
            const qidStr = String(row.question_id)
            const prevQAny = statusByQidAny[qidStr]
            statusByQidAny[qidStr] = mergeStatus(prevQAny, s)
          }
          
          const prevOwnerT = statusByOwnerTitle[titleNorm]
          statusByOwnerTitle[titleNorm] = mergeStatus(prevOwnerT, s)
          if (descNorm) {
            const prevOwnerD = statusByOwnerTitle[descNorm]
            statusByOwnerTitle[descNorm] = mergeStatus(prevOwnerD, s)
          }
        })
      } catch {}
      // Prefer question_id match first; then exact agency/title and fallbacks; finally fuzzy by title tokens.
      const candidatesAll = [...(existing || []), ...((anyRows || []))]
      const merged = (assignPreviewTasks || []).map(item => {
        const titleNorm = normalizeComplianceTitle(item.title)
        const descNormItem = normalizeComplianceTitle(item.description)
        const keyExact = `${assessmentId || ''}|${ownerAgencyId || ''}|${titleNorm}`
        const keyLegacy = `${assessmentId || ''}||${titleNorm}`
        const keyAny = `${assessmentId || ''}|||${titleNorm}`
        const dExact = descNormItem ? `${assessmentId || ''}|${ownerAgencyId || ''}|${descNormItem}` : null
        const dLegacy = descNormItem ? `${assessmentId || ''}||${descNormItem}` : null
        const dAny = descNormItem ? `${assessmentId || ''}|||${descNormItem}` : null
        const qKey = item.question_id ? `${assessmentId || ''}|${String(item.question_id)}` : null
        const qidCandidates = (Array.isArray(item?.question_ids) ? item.question_ids : [])
          .map(q => [ statusByQid[`${assessmentId || ''}|${String(q)}`], statusByQidAny[String(q)] ])
          .flat()
        const candidates = [
          qKey ? statusByQid[qKey] : undefined,
          item.question_id ? statusByQidAny[String(item.question_id)] : undefined,
          ...qidCandidates,
          statusByKey[keyExact], statusByKey[keyLegacy], statusByAny[keyAny],
          dExact ? statusByDescKey[dExact] : undefined,
          dLegacy ? statusByDescKey[dLegacy] : undefined,
          dAny ? statusByDescAny[dAny] : undefined,
          statusByOwnerTitle[titleNorm],
          descNormItem ? statusByOwnerTitle[descNormItem] : undefined
        ]
        let status = candidates.reduce((acc, s) => mergeStatus(acc, s), undefined)

        // Fallback: fuzzy match by title/description tokens with question_id boost
        if (!status) {
          const fuzzy = fuzzyStatusFromCandidates(item.title, candidatesAll, 0.65, item)
          status = fuzzy || status
        }
        const normalized = normalizeStatus(status)
        return normalized ? { ...item, status: normalized } : item
      })
      setAssignPreviewTasks(merged)
      setAssignAllCompleted(merged.length > 0 && merged.every(i => (i.status || '').toLowerCase() === 'completed'))
      setAssignHasInProgress(merged.some(i => (i.status || '').toLowerCase() === 'in_progress'))
      setAssignHasPending(merged.some(i => (i.status || '').toLowerCase() === 'pending'))
    } catch (e) {
      console.warn('Failed to refresh modal task statuses:', e)
    }
  }

  const handleViewCompliedTasks = () => {
    try { closeAssignModal() } catch {}
    navigate('/task-reminders')
  }

  const toIsoDate = (d) => {
    try {
      if (!d) return new Date().toISOString()
      const s = String(d).trim()
      let year, month, day
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        ;[year, month, day] = s.split('-')
      } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
        const [m, d2, y] = s.split('/')
        year = y
        month = m.padStart(2, '0')
        day = d2.padStart(2, '0')
      } else {
        const parsed = new Date(s)
        if (!isNaN(parsed)) return new Date(parsed.setUTCHours(0, 0, 0, 0)).toISOString()
        return new Date().toISOString()
      }
      return new Date(`${year}-${month}-${day}T00:00:00Z`).toISOString()
    } catch {
      return new Date().toISOString()
    }
  }

  // Confirm and create compliance tasks with selected dates
  const handleConfirmAssignCompliance = async () => {
    try {
      setAssignError(null)
      // Validate dates
      const startIso = toIsoDate(assignDates.startDate)
      const dueIso = toIsoDate(assignDates.dueDate)
      if (assignDates.startDate && assignDates.dueDate && new Date(assignDates.dueDate) < new Date(assignDates.startDate)) {
        setAssignError('Due date must be after start date.')
        return
      }

      const ownerId = assignOwnerId || selectedAssessmentForAssign?.user_id || selectedAssessmentForAssign?.userId || null
      const assessmentId = fullAssessmentForAssign?.id || selectedAssessmentForAssign?.id || null
      if (!assessmentId) {
        setAssignError('Assessment not loaded for assignment.')
        return
      }

      const itemsToAssign = (assignPreviewTasks || []).filter(t => t.include !== false)
      if (itemsToAssign.length === 0) {
        setAssignError('Select at least one item to assign.')
        return
      }

      // Build per-item payload honoring assignee choice and question_id when available
      const tasksPayload = itemsToAssign.flatMap(t => {
        const base = {
          title: t.title,
          description: t.description,
          category: t.category,
          priority: (t.priority || 'medium'),
          assigned_to: (t.assignee === 'me') ? user.id : ownerId,
          status: 'pending'
        }
        const qids = Array.isArray(t?.question_ids) && t.question_ids.length > 0
          ? t.question_ids
          : (t.question_id != null ? [String(t.question_id)] : [])
        if (qids.length > 0) {
          return qids.map(q => ({ ...base, question_id: String(q) }))
        }
        return [{ ...base, question_id: null }]
      })

      // Use owner agency_id when available for correct RLS scoping; fall back to current profile
      const overrides = {
        assessment_id: assessmentId,
        agency_id: assignOwnerAgencyId ?? profile?.agency_id ?? null,
        created_at: startIso,
        due_date: dueIso
      }

      // Create tasks in bulk
      const created = await dbHelpers.createTasksBulk(user.id, tasksPayload, overrides)
      if (!created || created.length === 0) {
        setAssignError('No tasks were created. Please try again.')
        return
      }

      setAssignJustCreated(true)
      // Reflect real status and due date from created rows per item immediately
      setAssignPreviewTasks(prev => {
        const byQid = new Map()
        const byTitle = new Map()
        ;(created || []).forEach(row => {
          const qid = row?.question_id != null ? String(row.question_id) : null
          const titleNorm = normalizeComplianceTitle(row?.title || '')
          if (qid) byQid.set(qid, row)
          if (titleNorm) byTitle.set(titleNorm, row)
        })
        return prev.map(t => {
          if (t.include === false) return t
          const qid = t?.question_id != null ? String(t.question_id) : null
          const titleNorm = normalizeComplianceTitle(t?.title || '')
          const qids = Array.isArray(t?.question_ids) ? t.question_ids.map(String) : (qid ? [qid] : [])
          const match = qids.map(q => byQid.get(q)).find(Boolean) || byTitle.get(titleNorm)
          if (match) {
            return {
              ...t,
              status: normalizeStatus(match.status) || 'pending',
              due_date: match.due_date || assignDates?.dueDate
            }
          }
          return {
            ...t,
            status: 'pending',
            due_date: assignDates?.dueDate
          }
        })
      })
      try { await fetchAgencyRiskScores() } catch {}
      try { await loadDashboardData() } catch {}
      try { await refreshAssignModalStatuses() } catch {}
      try { await loadUpcomingTasks() } catch {}
      // Dispatch event to notify Tasks Table and other components of new tasks
      window.dispatchEvent(new CustomEvent('taskUpdated'))
    } catch (e) {
      console.error('Assign compliance error:', e)
      setAssignError(e?.message || 'Failed to assign tasks.')
    }
  }

  // Assign compliance tasks to the assessment's user based on gaps
  const handleAssignCompliance = async (assessment) => {
    try {
      setLoading(true)
      // Fetch full assessment details to include recommendations/responses
      const run = async () => supabase
        .from('assessments')
        .select('*')
        .eq('id', assessment.id)
        .single()
      const { data: fullAssessment, error } = await executeWithAuthRetry(run)
      if (error) throw error
      // Derive agency_id from assessment owner for scoped task creation
      let ownerAgencyId = null
      try {
        const ownerId = (fullAssessment || assessment)?.user_id || (fullAssessment || assessment)?.userId || null
        if (ownerId) {
          const runOwner = async () => supabase
            .from('users')
            .select('id, agency_id')
            .eq('id', ownerId)
            .single()
          const { data: ownerProfile } = await executeWithAuthRetry(runOwner)
          ownerAgencyId = ownerProfile?.agency_id ?? null
        }
      } catch (e) {
        console.warn('Failed to fetch owner agency_id for quick assignment:', e)
      }
      // Assign tasks to the assessment owner
      // Create tasks as the current user (assigner), assign to assessment owner
      const created = await dbHelpers.assignComplianceTasksFromAssessment(
        user.id,
        fullAssessment || assessment,
        { agency_id: ownerAgencyId ?? profile?.agency_id ?? null }
      )
      logger.info('✅ Compliance tasks created:', created?.length || 0)
      // Immediately recompute agency widget compliance flags
      try { await fetchAgencyRiskScores() } catch {}
      // Refresh dashboard data to update button states
      try { await loadDashboardData() } catch {}
      // Reload tasks widget for current user if applicable
      if (user?.id === assessment.userId) {
        loadUpcomingTasks?.()
      }
    } catch (err) {
      logger.error('💥 Failed to assign compliance tasks:', err)
    } finally {
      setLoading(false)
    }
  }

  // Map zero-score assessment responses to scheduled Task Reminders
  const handleScheduleReminders = async (assessment) => {
    let scheduleError = null
    try {
      setLoading(true)

      // Attempt to load full assessment details; gracefully degrade on RLS errors
      let baseAssessment = assessment
      try {
        const run = async () => supabase
          .from('assessments')
          .select('*')
          .eq('id', assessment.id)
          .single()
        const { data: fullAssessment } = await executeWithAuthRetry(run)
        if (fullAssessment) {
          baseAssessment = fullAssessment
        }
      } catch (e) {
        // Likely RLS: not the owner of the assessment; continue with available data
        console.warn('ScheduleReminders: unable to fetch full assessment (continuing with partial):', e?.message || e)
      }

      // If we own the assessment, try to enrich with question map and explicit responses
      try {
        const ownerId = baseAssessment?.user_id || baseAssessment?.userId || null
        if (ownerId && ownerId === user?.id) {
          // Build question titles/categories for better mapping
          let questionTitles = {}
          let questionMap = {}
          try {
            const runQuestions = async () => supabase
              .from('assessment_questions')
              .select(`
                id, question_text,
                assessment_categories(name)
              `)
              .eq('is_active', true)
            const { data: questionsRows } = await executeWithAuthRetry(runQuestions)
            ;(questionsRows || []).forEach(q => {
              const catName = q?.assessment_categories?.name || 'General'
              questionTitles[q.id] = q.question_text
              questionMap[q.id] = { title: q.question_text, category: catName }
            })
          } catch (e) {
            console.warn('ScheduleReminders: failed to fetch assessment questions:', e)
          }
          // Fallback to explicit responses if embedded are missing
          let responsesObj = baseAssessment?.responses || {}
          if (!responsesObj || Object.keys(responsesObj).length === 0) {
            try {
              const runResponses = async () => supabase
                .from('assessment_responses')
                .select('question_id, response_value')
                .eq('assessment_id', baseAssessment.id)
              const { data: respRows } = await executeWithAuthRetry(runResponses)
              responsesObj = {}
              ;(respRows || []).forEach(r => {
                const val = typeof r.response_value === 'string' ? r.response_value : r.response_value?.value ?? r.response_value
                responsesObj[String(r.question_id)] = val
              })
            } catch (e) {
              console.warn('ScheduleReminders: failed to fetch explicit responses:', e)
            }
          }
          baseAssessment = { ...baseAssessment, questions: questionMap, question_titles: questionTitles, responses: responsesObj }
        }
      } catch (e) {
        console.warn('ScheduleReminders: enrichment skipped due to error:', e)
      }

      // Resolve agency_id for RLS scoping
      let ownerAgencyId = null
      try {
        const ownerId = baseAssessment?.user_id || baseAssessment?.userId || null
        if (ownerId) {
          const runOwner = async () => supabase
            .from('users')
            .select('id, agency_id')
            .eq('id', ownerId)
            .single()
          const { data: ownerProfile } = await executeWithAuthRetry(runOwner)
          ownerAgencyId = ownerProfile?.agency_id ?? null
        }
      } catch (e) {
        console.warn('ScheduleReminders: failed to fetch owner agency_id:', e)
      }

      // Default due date for reminders: 14 days from now
      const dueDateIso = new Date(Date.now() + 1000 * 60 * 60 * 24 * 14).toISOString()

      // Integrate visible Non-compliant answers into assessment for task generation
      try {
        const noItems = noResponseMap?.[assessment.id]
        if (Array.isArray(noItems) && noItems.length > 0) {
          const respObj = { ...(baseAssessment?.responses || {}) }
          const qTitles = { ...(baseAssessment?.question_titles || {}) }
          const qMap = { ...(baseAssessment?.questions || {}) }
          noItems.forEach(item => {
            const qid = String(item.question_id)
            respObj[qid] = 'no'
            if (item.question_text) qTitles[qid] = item.question_text
            qMap[qid] = { title: item.question_text || qTitles[qid] || `Question ${qid}`, category: item.category || (qMap[qid]?.category) || 'General' }
          })
          baseAssessment = { ...baseAssessment, responses: respObj, question_titles: qTitles, questions: qMap }
        }
      } catch (e) {
        console.warn('ScheduleReminders: failed to integrate non-compliant list:', e)
      }

      // Create tasks derived from zero-score responses
      // Note: Use default 'pending' status to satisfy DB enum constraints
      try {
        const created = await dbHelpers.assignComplianceTasksFromAssessment(
          user.id,
          baseAssessment,
          { agency_id: ownerAgencyId ?? profile?.agency_id ?? null, due_date: dueDateIso }
        )
        logger.info('✅ Scheduled reminder tasks created:', created?.length || 0)
      } catch (e) {
        scheduleError = e
        logger.error('💥 assignComplianceTasksFromAssessment failed:', e)
      }

      // Refresh widgets regardless
      try { await fetchAgencyRiskScores() } catch {}
      try { await loadDashboardData() } catch {}
      try { await loadUpcomingTasks() } catch {}
    } catch (err) {
      scheduleError = err
      logger.error('💥 Failed to schedule task reminders:', err)
    } finally {
      setLoading(false)
      // Navigate to Task Reminders even if scheduling fails, so user sees current tasks
      navigate('/task-reminders')
      // Provide lightweight inline feedback when something went wrong
      if (scheduleError) {
        try {
          // Non-blocking, minimal feedback without adding new UI components
          alert('Failed to schedule reminders. You may not have permission to access assessment details.')
        } catch (_) {}
      }
    }
  }

  // Export report function
  const handleExportReport = () => {
    const reportData = {
      timestamp: new Date().toISOString(),
      timeframe: selectedTimeframe,
      analyticsView: analyticsView,
      securityScore: dashboardData.score,
      metrics: analyticsData.securityMetrics,
      categories: categoryData.map(cat => ({
        name: cat.name,
        score: cat.score,
        percentage: Math.round((cat.score / cat.total) * 100)
      })),
      tasks: {
        total: upcomingTasks.length,
        pending: upcomingTasks.filter(task => task.priority === 'high').length,
        completed: dashboardData.tasks?.filter(task => task.status === 'completed').length || 0
      }
    }
    
    const dataStr = JSON.stringify(reportData, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `cybersecurity-report-${selectedTimeframe}-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      
      // Enhanced assessment fetching with multiple fallback strategies
      let latestAssessment = null
      let assessmentError = null
      
      // Strategy 1: Try with completed_at ordering (preferred)
      try {
        const run1 = async () => supabase
          .from('assessments')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'completed')
          .not('completed_at', 'is', null)
          .order('completed_at', { ascending: false })
          .limit(1)
          .single()
        const { data, error } = await executeWithAuthRetry(run1)
        
        latestAssessment = data
        assessmentError = error
        console.log('Strategy 1 (completed_at order):', { data, error })
      } catch (e) {
        assessmentError = e
        console.log('Strategy 1 failed:', e.message)
      }
      
      // Strategy 2: Fallback to created_at ordering if completed_at fails
      if (assessmentError && assessmentError.code === 'PGRST116') {
        try {
          const run2 = async () => supabase
            .from('assessments')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'completed')
            .order('created_at', { ascending: false })
            .limit(1)
            .single()
          const { data, error } = await executeWithAuthRetry(run2)
          
          latestAssessment = data
          assessmentError = error
          console.log('Strategy 2 (created_at order):', { data, error })
        } catch (e) {
          assessmentError = e
          console.log('Strategy 2 failed:', e.message)
        }
      }
      
      // Strategy 3: Get all completed assessments and pick the latest
      if (assessmentError && assessmentError.code === 'PGRST116') {
        try {
          const run3 = async () => supabase
            .from('assessments')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'completed')
            .order('created_at', { ascending: false })
          const { data, error } = await executeWithAuthRetry(run3)
          
          if (!error && data && data.length > 0) {
            // Pick the most recent one based on completed_at or created_at
            latestAssessment = data.sort((a, b) => {
              const dateA = new Date(a.completed_at || a.created_at)
              const dateB = new Date(b.completed_at || b.created_at)
              return dateB - dateA
            })[0]
            assessmentError = null
            console.log('Strategy 3 (all assessments):', { latest: latestAssessment, total: data.length })
          } else {
            assessmentError = error || new Error('No completed assessments found')
          }
        } catch (e) {
          assessmentError = e
          console.log('Strategy 3 failed:', e.message)
        }
      }
      
      console.log('Final Latest Assessment:', latestAssessment)
      console.log('Final Assessment Error:', assessmentError)
      
      // Test: Check if we have any assessments at all
      const runAll = async () => supabase
        .from('assessments')
        .select('*')
        .eq('user_id', user.id)
      const { data: allAssessments, error: allError } = await executeWithAuthRetry(runAll)
      
      console.log('All user assessments:', allAssessments)
      console.log('All assessments error:', allError)
      
      if (assessmentError && assessmentError.code !== 'PGRST116') {
        console.warn('Assessment loading warning:', assessmentError)
      }

      // Backfill task categories and missing assessment_id based on latest assessment-derived compliance tasks
      try {
        if (latestAssessment) {
          const updatedCount = await dbHelpers.backfillTaskCategoriesFromAssessment(latestAssessment)
          const filledCount = await backfillAssessmentIdFromAssessment(latestAssessment)
          console.log('Backfill categories updated tasks:', updatedCount)
          console.log('Backfilled assessment_id on legacy tasks:', filledCount)
        }
      } catch (e) {
        console.warn('Backfill helpers failed during dashboard load:', e)
      }
      
      // Get assessment history
      const runHistory = async () => supabase
        .from('assessments')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(10)
      const { data: history, error: historyError } = await executeWithAuthRetry(runHistory)
      
      if (historyError) throw historyError
      
      // Get tasks assigned to or created by the current user
      // Include completed so categories can reflect compliance
      const runTasks = async () => supabase
        .from('tasks')
        .select('*')
        .or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`)
        .order('due_date', { ascending: true })
        .limit(100)
      const { data: tasks, error: tasksError } = await executeWithAuthRetry(runTasks)
      
      if (tasksError) throw tasksError
      
      // Process recommendations and categories from latest assessment
      const recommendations = latestAssessment?.recommendations || []
      const responses = latestAssessment?.responses || {}
      
      console.log('Assessment Responses:', responses)
      console.log('Assessment Recommendations:', recommendations)
      
      // Calculate category scores if we have assessment data
      let categories = {}
      if (latestAssessment && Object.keys(responses).length > 0) {
        console.log('Processing category scores...')
        // Get questions to calculate category scores
        const runQuestions = async () => supabase
          .from('assessment_questions')
          .select(`
            *,
            assessment_categories(name)
          `)
          .eq('is_active', true)
        const { data: questions, error: questionsError } = await executeWithAuthRetry(runQuestions)
        
        console.log('Questions query error:', questionsError)
        console.log('Questions count:', questions?.length || 0)
        
        if (questions) {
          console.log('Questions from database:', questions)
          const categoryScores = {}
          const categoryTotals = {}
          
          // Map database category names to component category keys
          const categoryMapping = {
            'Network Security': 'networkSecurity',
            'Data Protection': 'dataProtection', 
            'Access Control': 'accessControl',
            'Incident Response': 'incidentResponse',
            'Security Awareness': 'training',
            'Physical Security': 'physicalSecurity',
            'Compliance & Governance': 'compliance',
            'Business Continuity': 'businessContinuity'
          }
          
          questions.forEach(q => {
            const categoryName = q.assessment_categories?.name || 'General'
            const categoryKey = categoryMapping[categoryName] || 'general'
            
            console.log(`Processing question ${q.id}: category=${categoryName}, key=${categoryKey}, response=${responses[q.id]}`)
            
            if (!categoryScores[categoryKey]) {
              categoryScores[categoryKey] = 0
              categoryTotals[categoryKey] = 0
            }
            categoryTotals[categoryKey]++
            if (responses[q.id] === 'yes') {
              categoryScores[categoryKey]++
            }
          })
          
          console.log('Category scores:', categoryScores)
          console.log('Category totals:', categoryTotals)
          
          // Convert to percentages
          Object.keys(categoryScores).forEach(category => {
            categories[category] = {
              score: categoryTotals[category] > 0 ? (categoryScores[category] / categoryTotals[category]) * 100 : 0,
              total: categoryTotals[category]
            }
          })
        }
      }
      
      console.log('Final categories:', categories)
      // Compute latest assessment-derived compliance task titles for consistent counting
      const latestCompliancePreview = latestAssessment
        ? (dbHelpers.generateComplianceTasksFromAssessment(latestAssessment) || [])
        : []
      const latestComplianceTitles = latestCompliancePreview.map(t => t.title).filter(Boolean)

      console.log('Final dashboard data being set:', {
        score: latestAssessment?.score || 0,
        riskLevel: latestAssessment?.risk_level || 'medium',
        lastAssessmentDate: latestAssessment?.completed_at || null,
        assessmentHistory: history || [],
        recommendations: recommendations,
        categories: categories,
        tasks: tasks || [],
        complianceTitles: latestComplianceTitles
      })

      setDashboardData({
        score: latestAssessment?.score || 0,
        riskLevel: latestAssessment?.risk_level || 'medium',
        lastAssessmentDate: latestAssessment?.completed_at || null,
        assessmentHistory: history || [],
        recommendations: recommendations,
        categories: categories,
        tasks: tasks || [],
        complianceTitles: latestComplianceTitles,
        compliancePreview: latestCompliancePreview
      })
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAgencyRiskScores = async () => {
    try {
      logger.debug('🔄 Fetching assessments with agency names...')

      // Fetch all completed assessments with user data including agency names
      const run = async () => supabase
        .from('assessments')
        .select(`
          id,
          title,
          score,
          risk_level,
          completed_at,
          created_at,
          status,
          user_id,
          users!inner(
            id,
            name,
            email,
            position,
            agency
          )
        `)
        .eq('status', 'completed')
        .not('users.agency', 'is', null)
        .order('completed_at', { ascending: false })

      const { data: assessmentsData, error: assessmentsError } = await executeWithAuthRetry(run)

      if (!assessmentsError && assessmentsData && assessmentsData.length > 0) {
        logger.debug('✅ Fetched assessments data:', assessmentsData)
        
        // Format the data to include all assessments with agency information
        const formattedAssessments = assessmentsData.map(assessment => ({
          id: assessment.id,
          title: assessment.title || 'Security Assessment',
          score: assessment.score,
          riskLevel: assessment.risk_level,
          status: assessment.status,
          completedAt: assessment.completed_at,
          createdAt: assessment.created_at,
          userId: assessment.user_id,
          agency: assessment.users?.agency || 'Unknown Agency',
          userInfo: {
            id: assessment.users?.id,
            name: assessment.users?.name || 'Unknown User',
            email: assessment.users?.email,
            position: assessment.users?.position
          }
        }))

        // Group by agency and keep only the latest assessment per agency
        const latestPerAgencyMap = formattedAssessments.reduce((acc, item) => {
          const key = item.agency || 'Unknown Agency'
          const current = acc[key]
          const itemTime = new Date(item.completedAt || item.createdAt || 0)
          const currentTime = new Date(current?.completedAt || current?.createdAt || 0)
          if (!current || itemTime > currentTime) {
            acc[key] = item
          }
          return acc
        }, {})

        const latestPerAgency = Object.values(latestPerAgencyMap)
          .sort((a, b) => new Date(b.completedAt || b.createdAt || 0) - new Date(a.completedAt || a.createdAt || 0))

        // Enrich each assessment with compliance completion status (map from Tasks table)
        const enriched = await Promise.all(latestPerAgency.map(async (item) => {
          try {
            // Load full assessment to generate preview compliance tasks
            const runFull = async () => supabase
              .from('assessments')
              .select('*')
              .eq('id', item.id)
              .single()
            const { data: fullAssessment } = await executeWithAuthRetry(runFull)
            const base = fullAssessment || item
            const preview = dbHelpers.generateComplianceTasksFromAssessment(base) || []

            if (!item.userId || preview.length === 0) {
              return { ...item, complianceAllCompleted: false, complianceHasInProgress: false }
            }

            const titles = preview.map(t => t.title)
            const previewTitleSet = new Set((preview || []).map(t => normalizeComplianceTitle(t.title)))
            const previewDescSet = new Set((preview || []).map(t => normalizeComplianceTitle(t.description)))
            // Fetch owner's agency_id for per-agency scoping
            const runOwner = async () => supabase
              .from('users')
              .select('id, agency_id')
              .eq('id', item.userId)
              .single()
            const { data: ownerProfile } = await executeWithAuthRetry(runOwner)
            const ownerAgencyId = ownerProfile?.agency_id ?? null
            // Build dynamic conditions to include tasks assigned to either the agency owner or current user
            let orCond = `assigned_to.eq.${item.userId},user_id.eq.${item.userId}`
            if (user?.id && user.id !== item.userId) {
              orCond += `,assigned_to.eq.${user.id},user_id.eq.${user.id}`
            }
            // Try selecting with question_id; if column missing, retry without it
            const runTasks = async (includeQid = true) => supabase
              .from('tasks')
              .select(includeQid ? 'id,title,description,status,assigned_to,user_id,agency_id,assessment_id,question_id' : 'id,title,description,status,assigned_to,user_id,agency_id,assessment_id')
              .eq('assessment_id', item.id)
              .or(orCond)
            let existing = []
            {
              const { data, error } = await executeWithAuthRetry(() => runTasks(true))
              if (!error) {
                existing = data || []
              } else {
                const msg = String(error?.message || '').toLowerCase()
                const qidMissing = msg.includes("could not find the 'question_id' column") || msg.includes('column question_id does not exist') || (msg.includes('could not find') && msg.includes('question_id') && msg.includes('schema cache'))
                if (qidMissing) {
                  const { data: data2, error: err2 } = await executeWithAuthRetry(() => runTasks(false))
                  if (!err2) {
                    existing = data2 || []
                  } else {
                    existing = []
                  }
                } else {
                  existing = []
                }
              }
            }

            // Index statuses using question_id, title, and description keys, normalized
            const statusByKey = {}
            const statusByAny = {}
            const statusByOwnerTitle = {}
            const statusByQid = {}
            const statusByQidAny = {}
            const statusByDescKey = {}
            const statusByDescAny = {}
            ;(existing || []).forEach(row => {
              const titleNorm = normalizeComplianceTitle(row.title)
              const descNorm = normalizeComplianceTitle(row.description)
              const a = row.assessment_id || ''
              const ag = row.agency_id || ''
              const s = normalizeStatus(row.status) || 'pending'
              // Map by question_id when present
              if (row.question_id) {
                const qidStr = String(row.question_id)
                const qExactKey = `${a}|${qidStr}`
                const prevQExact = statusByQid[qExactKey]
                statusByQid[qExactKey] = mergeStatus(prevQExact, s)
                const prevQAny = statusByQidAny[qidStr]
                statusByQidAny[qidStr] = mergeStatus(prevQAny, s)
              }
              // Title/Description-based maps
              if (previewTitleSet.has(titleNorm) || (descNorm && previewDescSet.has(descNorm))) {
                const keyExact = `${a}|${ag}|${titleNorm}`
                const prevExact = statusByKey[keyExact]
                statusByKey[keyExact] = mergeStatus(prevExact, s)

                const keyLegacy = `${a}||${titleNorm}`
                const prevLegacy = statusByKey[keyLegacy]
                statusByKey[keyLegacy] = mergeStatus(prevLegacy, s)

                const keyAny = `${a}|||${titleNorm}`
                const prevAny = statusByAny[keyAny]
                statusByAny[keyAny] = mergeStatus(prevAny, s)

                if (descNorm) {
                  const dExact = `${a}|${ag}|${descNorm}`
                  const prevDExact = statusByDescKey[dExact]
                  statusByDescKey[dExact] = mergeStatus(prevDExact, s)
                  const dLegacy = `${a}||${descNorm}`
                  const prevDLegacy = statusByDescKey[dLegacy]
                  statusByDescKey[dLegacy] = mergeStatus(prevDLegacy, s)
                  const dAny = `${a}|||${descNorm}`
                  const prevDAny = statusByDescAny[dAny]
                  statusByDescAny[dAny] = mergeStatus(prevDAny, s)
                }

                const isOwnerRow = row.assigned_to === item.userId || row.user_id === item.userId
                if (isOwnerRow) {
                  const prevOwnerT = statusByOwnerTitle[titleNorm]
                  statusByOwnerTitle[titleNorm] = mergeStatus(prevOwnerT, s)
                  if (descNorm) {
                    const prevOwnerD = statusByOwnerTitle[descNorm]
                    statusByOwnerTitle[descNorm] = mergeStatus(prevOwnerD, s)
                  }
                }
              }
            })

            // Also search owner's tasks across assessments for matching titles and question_id
            try {
              const runTasksAny = async (includeQid = true) => supabase
                .from('tasks')
                .select(includeQid ? 'id,title,description,status,assigned_to,user_id,agency_id,assessment_id,question_id' : 'id,title,description,status,assigned_to,user_id,agency_id,assessment_id')
                .or(`assigned_to.eq.${item.userId},user_id.eq.${item.userId}${user?.id && user.id !== item.userId ? `,assigned_to.eq.${user.id},user_id.eq.${user.id}` : ''}${ownerAgencyId ? `,agency_id.eq.${ownerAgencyId}` : ''}`)
              let anyRows = []
              {
                const { data, error } = await executeWithAuthRetry(() => runTasksAny(true))
                if (!error) {
                  anyRows = data || []
                } else {
                  const msg = String(error?.message || '').toLowerCase()
                  const qidMissing = msg.includes("could not find the 'question_id' column") || msg.includes('column question_id does not exist') || (msg.includes('could not find') && msg.includes('question_id') && msg.includes('schema cache'))
                  if (qidMissing) {
                    const { data: data2, error: err2 } = await executeWithAuthRetry(() => runTasksAny(false))
                    if (!err2) {
                      anyRows = data2 || []
                    }
                  }
                }
              }
              ;(anyRows || []).forEach(row => {
                const titleNorm = normalizeComplianceTitle(row.title)
                const descNorm = normalizeComplianceTitle(row.description)
                const s = normalizeStatus(row.status) || 'pending'
                if (row.question_id) {
                  const qidStr = String(row.question_id)
                  const prevQAny = statusByQidAny[qidStr]
                  statusByQidAny[qidStr] = mergeStatus(prevQAny, s)
                }
                const prevOwnerT = statusByOwnerTitle[titleNorm]
                statusByOwnerTitle[titleNorm] = mergeStatus(prevOwnerT, s)
                if (descNorm) {
                  const prevOwnerD = statusByOwnerTitle[descNorm]
                  statusByOwnerTitle[descNorm] = mergeStatus(prevOwnerD, s)
                }
              })
            } catch {}

            // Compute per-item status by merging all candidate sources with precedence
            const allStatuses = preview.map(p => {
              const titleNorm = normalizeComplianceTitle(p.title)
              const descNormItem = normalizeComplianceTitle(p.description)
              const qKey = p.question_id ? `${item.id || ''}|${String(p.question_id)}` : null
              const keyExact = `${item.id || ''}|${ownerAgencyId || ''}|${titleNorm}`
              const keyLegacy = `${item.id || ''}||${titleNorm}`
              const keyAny = `${item.id || ''}|||${titleNorm}`
              const dExact = descNormItem ? `${item.id || ''}|${ownerAgencyId || ''}|${descNormItem}` : null
              const dLegacy = descNormItem ? `${item.id || ''}||${descNormItem}` : null
              const dAny = descNormItem ? `${item.id || ''}|||${descNormItem}` : null

              const candidates = [
                qKey ? statusByQid[qKey] : undefined,
                p.question_id ? statusByQidAny[String(p.question_id)] : undefined,
                statusByKey[keyExact],
                statusByKey[keyLegacy],
                statusByAny[keyAny],
                dExact ? statusByDescKey[dExact] : undefined,
                dLegacy ? statusByDescKey[dLegacy] : undefined,
                dAny ? statusByDescAny[dAny] : undefined,
                statusByOwnerTitle[titleNorm],
                descNormItem ? statusByOwnerTitle[descNormItem] : undefined
              ]

              const merged = candidates.reduce((acc, s) => mergeStatus(acc, s), undefined)
              return normalizeStatus(merged) || 'pending'
            })

            const allCompleted = allStatuses.length > 0 && allStatuses.every(s => s === 'completed')
            // Treat both 'in_progress' and 'pending' as active for UI button state
            const anyInProgress = allStatuses.some(s => s === 'in_progress' || s === 'pending')
            const anyPending = allStatuses.some(s => s === 'pending')
            const countCompleted = allStatuses.filter(s => s === 'completed').length
            const countInProgress = allStatuses.filter(s => s === 'in_progress').length
            const countPending = allStatuses.filter(s => s === 'pending').length
            
            // Debug logging for compliance status
            console.log(`🔍 Assessment ${item.id} compliance debug:`, {
              allStatuses,
              allCompleted,
              anyInProgress,
              anyPending,
              countCompleted,
              countInProgress,
              countPending,
              totalTasks: allStatuses.length
            })
            
            return { 
              ...item, 
              complianceAllCompleted: allCompleted, 
              complianceHasInProgress: anyInProgress, 
              complianceHasPending: anyPending,
              complianceCountCompleted: countCompleted,
              complianceCountInProgress: countInProgress,
              complianceCountPending: countPending
            }
          } catch (e) {
            console.warn('Failed to compute compliance status for assessment:', item?.id, e)
            return { ...item, complianceAllCompleted: false, complianceHasInProgress: false, complianceHasPending: false }
          }
        }))

        setAgencyRiskScores(enriched)
        logger.info('✅ Set latest assessments per agency:', enriched?.length || 0)
        return
      }

      logger.warn('⚠️ No assessment data found, setting empty array')
      setAgencyRiskScores([])
      
    } catch (error) {
      logger.error('💥 Error fetching assessments:', error)
      setAgencyRiskScores([])
    }
  }

  const getUserTypeLabel = () => {
    return getTypeLabel(profile?.user_type) || 'User'
  }

  // Function to get view-specific summary data
  const getViewSummary = () => {
    switch (analyticsView) {
      case 'detailed':
        return {
          title: 'Detailed Security Metrics',
          description: 'Comprehensive analysis of security performance indicators',
          highlight: `${analyticsData.securityMetrics.vulnerabilities.current} active vulnerabilities`
        }
      case 'trends':
        return {
          title: 'Trend Analysis',
          description: `Security trends over ${selectedTimeframe.replace(/([0-9]+)/, '$1 ')}`,
          highlight: `${analyticsData.monthlyTrends.security_score[analyticsData.monthlyTrends.security_score.length - 1]}% current score`
        }
      case 'comparison':
        return {
          title: 'Industry Benchmarking',
          description: 'Performance comparison with industry standards',
          highlight: `${analyticsData.industryComparison.security_score.yours}% vs ${analyticsData.industryComparison.security_score.industry}% industry avg`
        }
      default:
        return {
          title: 'Security Overview',
          description: 'High-level security posture summary',
          highlight: `${dashboardData.score || 85}% overall security score`
        }
    }
  }

  const getRiskLevelColor = () => {
    switch (dashboardData.riskLevel) {
      case 'low': return 'bg-success-500 text-white'
      case 'medium': return 'bg-warning-500 text-white'
      case 'high': return 'bg-security-500 text-white'
      default: return 'bg-cyber-500 text-white'
    }
  }

  const getRiskLevelIcon = () => {
    switch (dashboardData.riskLevel) {
      case 'low': return '✅'
      case 'medium': return '⚠️'
      case 'high': return '🚨'
      default: return '📊'
    }
  }

  // Enhanced mock data for advanced analytics with timeframe filtering
  const mockTrends = {
    '1month': [65, 68, 70, 72],
    '3months': [45, 52, 58, 65, 68, 70, 72, 75, 78, 80, 82, 85],
    '6months': [30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 88, 90, 92, 95, 98, 100, 95, 92, 88, 85, 82, 80],
    '1year': [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 88, 90, 92, 95, 98, 100, 95, 92, 88, 85, 82, 80, 78, 75, 72, 70, 68, 65, 62, 60, 58, 55]
  }

  // Function to filter data based on selected timeframe
  const getTimeframeData = (data, timeframe) => {
    const timeframeMap = {
      '1month': 4,
      '3months': 12, 
      '6months': 24,
      '1year': 36
    }
    const dataPoints = timeframeMap[timeframe] || 12
    return data.slice(-dataPoints)
  }

  // Function to calculate metrics based on timeframe
  const getTimeframeMetrics = (baseMetrics, timeframe) => {
    const multipliers = {
      '1month': 0.3,
      '3months': 1,
      '6months': 1.8,
      '1year': 3.2
    }
    const multiplier = multipliers[timeframe] || 1
    
    return {
      vulnerabilities: Math.max(1, Math.round(baseMetrics.vulnerabilities * multiplier)),
      incidents: Math.max(1, Math.round(baseMetrics.incidents * multiplier)),
      resolved: Math.max(1, Math.round(baseMetrics.resolved * multiplier)),
      compliance: Math.min(100, Math.round(baseMetrics.compliance + (multiplier - 1) * 5))
    }
  }

  // Advanced analytics data - now using real data with dynamic filtering
  const analyticsData = useMemo(() => {
    // Calculate real metrics from dashboard data
    const totalTasks = upcomingTasks.length + (dashboardData.tasks?.length || 0);
    const completedTasks = dashboardData.tasks?.filter(task => task.status === 'completed').length || 0;
    const pendingTasks = upcomingTasks.length;
    const overdueTasks = dashboardData.tasks?.filter(task => {
      if (task.status === 'completed') return false;
      if (!task.due_date) return false;
      return new Date(task.due_date) < new Date();
    }).length || 0;
    
    // Calculate completion rate
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    
    // Calculate response time based on task completion patterns
    const avgResponseTime = overdueTasks > 0 ? 3.2 : pendingTasks > 5 ? 2.8 : 1.5;
    
    // Calculate prevention rate based on security score
    const preventionRate = dashboardData.score || 85;
    
    // Calculate awareness based on training category score
    const awarenessRate = dashboardData.categories?.training?.score || 75;
    
    // Base metrics for timeframe calculations
    const baseMetrics = {
      vulnerabilities: overdueTasks,
      incidents: Math.max(Math.floor(overdueTasks / 2), 1),
      resolved: completedTasks,
      compliance: Math.round(dashboardData.score || 85)
    };
    
    // Apply timeframe filtering to metrics
    const timeframeMetrics = getTimeframeMetrics(baseMetrics, selectedTimeframe);
    
    return {
      securityMetrics: {
        vulnerabilities: { 
          current: timeframeMetrics.vulnerabilities, 
          previous: Math.max(timeframeMetrics.vulnerabilities + 2, 1), 
          trend: timeframeMetrics.vulnerabilities <= 2 ? 'down' : 'up' 
        },
        incidents: { 
          current: timeframeMetrics.incidents, 
          previous: Math.max(timeframeMetrics.incidents + 1, 2), 
          trend: 'down' 
        },
        compliance: { 
          current: timeframeMetrics.compliance, 
          previous: Math.max(timeframeMetrics.compliance - 5, 70), 
          trend: 'up' 
        },
        training: { 
          current: Math.round(awarenessRate), 
          previous: Math.round(awarenessRate - 8), 
          trend: 'up' 
        }
      },
      performanceMetrics: {
        responseTime: { current: avgResponseTime, previous: avgResponseTime + 0.8, trend: 'down', unit: 'hours' },
        resolution: { current: completionRate, previous: Math.max(completionRate - 7, 70), trend: 'up', unit: '%' },
        prevention: { current: Math.round(preventionRate), previous: Math.round(preventionRate - 5), trend: 'up', unit: '%' },
        awareness: { current: Math.round(awarenessRate), previous: Math.round(awarenessRate - 7), trend: 'up', unit: '%' }
      },
      riskDistribution: [
        { name: 'Critical', value: 5, color: '#DC2626' },
        { name: 'High', value: 12, color: '#EA580C' },
        { name: 'Medium', value: 28, color: '#D97706' },
        { name: 'Low', value: 45, color: '#65A30D' },
        { name: 'Minimal', value: 10, color: '#059669' }
      ],
      complianceBreakdown: [
        { framework: 'NIST', score: 92, target: 95, items: 45 },
        { framework: 'ISO 27001', score: 88, target: 90, items: 38 },
        { framework: 'GDPR', score: 96, target: 98, items: 22 },
        { framework: 'HIPAA', score: 85, target: 95, items: 31 }
      ],
      monthlyTrends: {
        security_score: getTimeframeData([72, 75, 78, 80, 82, 85, 87, 89, 91, 88, 90, 92, 94, 96, 95, 93, 91, 89, 87, 85, 83, 81, 79, 77], selectedTimeframe),
        incidents: getTimeframeData([8, 6, 4, 7, 5, 3, 4, 2, 3, 1, 2, 3, 2, 1, 3, 4, 5, 6, 4, 3, 2, 1, 2, 3], selectedTimeframe),
        compliance: getTimeframeData([82, 84, 86, 88, 90, 91, 92, 93, 94, 93, 94, 94, 95, 96, 97, 96, 95, 94, 93, 92, 91, 90, 89, 88], selectedTimeframe),
        training: getTimeframeData([65, 68, 70, 72, 74, 76, 78, 79, 80, 78, 79, 78, 80, 82, 84, 83, 81, 79, 77, 75, 73, 71, 69, 67], selectedTimeframe)
      },
      industryComparison: {
        security_score: { yours: 85, industry: 78, government: 82 },
        compliance: { yours: 94, industry: 87, government: 91 },
        incidents: { yours: 3, industry: 8, government: 5 },
        training: { yours: 78, industry: 65, government: 72 }
      }
    };
  }, [upcomingTasks, dashboardData, selectedTimeframe, analyticsView])

  // Map completed tasks by category so the widget can reflect compliance
  const completedByCategory = useMemo(() => {
    const counts = {
      dataProtection: 0,
      accessControl: 0,
      networkSecurity: 0,
      incidentResponse: 0,
      training: 0,
      compliance: 0
    }
    const normalize = (cat) => {
      if (!cat) return 'training'
      const key = String(cat)
      if (key === 'dataProtection' || key === 'data-protection') return 'dataProtection'
      if (key === 'accessControl' || key === 'access-control') return 'accessControl'
      if (key === 'networkSecurity' || key === 'network-security') return 'networkSecurity'
      if (key === 'incidentResponse' || key === 'incident-response') return 'incidentResponse'
      if (key === 'training') return 'training'
      if (key === 'compliance') return 'compliance'
      return 'training'
    }
    try {
      (dashboardData?.tasks || []).forEach(t => {
        if ((t.status || '').toLowerCase() === 'completed') {
          const n = normalize(t.category)
          if (counts[n] !== undefined) counts[n]++
        }
      })
    } catch (e) {
      console.warn('Error computing completedByCategory:', e)
    }
    return counts
  }, [dashboardData?.tasks])

  // Map lacking-to-comply tasks assigned to current user by category
  const incompleteAssignedByCategory = useMemo(() => {
    const counts = {
      dataProtection: 0,
      accessControl: 0,
      networkSecurity: 0,
      incidentResponse: 0,
      training: 0,
      compliance: 0
    }
    const normalize = (cat) => {
      if (!cat) return 'training'
      const key = String(cat)
      if (key === 'dataProtection' || key === 'data-protection') return 'dataProtection'
      if (key === 'accessControl' || key === 'access-control') return 'accessControl'
      if (key === 'networkSecurity' || key === 'network-security') return 'networkSecurity'
      if (key === 'incidentResponse' || key === 'incident-response') return 'incidentResponse'
      if (key === 'training') return 'training'
      if (key === 'compliance') return 'compliance'
      return 'training'
    }
    const sinceDate = dashboardData?.lastAssessmentDate ? new Date(dashboardData.lastAssessmentDate) : null
    const titlesSet = new Set(dashboardData?.complianceTitles || [])
    try {
      (dashboardData?.tasks || []).forEach(t => {
        const status = (t.status || '').toLowerCase()
        const isIncomplete = status !== 'completed'
        const isAssignedToUser = String(t.assigned_to || '') === String(user?.id || '')
        const isCompliance = ((t.title || '').toLowerCase().includes('[compliance]'))
        const afterLatest = !sinceDate || (t.created_at && new Date(t.created_at) >= sinceDate)
        const inLatestSet = titlesSet.size === 0 || titlesSet.has(t.title)
        if (isIncomplete && isAssignedToUser && isCompliance && afterLatest && inLatestSet) {
          const n = normalize(t.category)
          if (counts[n] !== undefined) counts[n]++
        }
      })
    } catch (e) {
      console.warn('Error computing incompleteAssignedByCategory:', e)
    }
    return counts
  }, [dashboardData?.tasks, user?.id])

  // Map completed tasks assigned to current user by category
  const completedAssignedByCategory = useMemo(() => {
    const counts = {
      dataProtection: 0,
      accessControl: 0,
      networkSecurity: 0,
      incidentResponse: 0,
      training: 0,
      compliance: 0
    }
    const normalize = (cat) => {
      if (!cat) return 'training'
      const key = String(cat)
      if (key === 'dataProtection' || key === 'data-protection') return 'dataProtection'
      if (key === 'accessControl' || key === 'access-control') return 'accessControl'
      if (key === 'networkSecurity' || key === 'network-security') return 'networkSecurity'
      if (key === 'incidentResponse' || key === 'incident-response') return 'incidentResponse'
      if (key === 'training') return 'training'
      if (key === 'compliance') return 'compliance'
      return 'training'
    }
    const sinceDate = dashboardData?.lastAssessmentDate ? new Date(dashboardData.lastAssessmentDate) : null
    const titlesSet = new Set(dashboardData?.complianceTitles || [])
    try {
      (dashboardData?.tasks || []).forEach(t => {
        const status = (t.status || '').toLowerCase()
        const isCompleted = status === 'completed'
        const assignee = String(t.assigned_to ?? t.user_id ?? '')
        const isAssignedToUser = assignee === String(user?.id || '')
        const isCompliance = ((t.title || '').toLowerCase().includes('[compliance]'))
        const afterLatest = !sinceDate || (t.created_at && new Date(t.created_at) >= sinceDate)
        const inLatestSet = titlesSet.size === 0 || titlesSet.has(t.title)
        if (isCompleted && isAssignedToUser && isCompliance && afterLatest && inLatestSet) {
          const n = normalize(t.category)
          if (counts[n] !== undefined) counts[n]++
        }
      })
    } catch (e) {
      console.warn('Error computing completedAssignedByCategory:', e)
    }
    return counts
  }, [dashboardData?.tasks, user?.id])

  // Map in-progress tasks assigned to current user by category
  const inProgressAssignedByCategory = useMemo(() => {
    const counts = {
      dataProtection: 0,
      accessControl: 0,
      networkSecurity: 0,
      incidentResponse: 0,
      training: 0,
      compliance: 0
    }
    const normalize = (cat) => {
      if (!cat) return 'training'
      const key = String(cat)
      if (key === 'dataProtection' || key === 'data-protection') return 'dataProtection'
      if (key === 'accessControl' || key === 'access-control') return 'accessControl'
      if (key === 'networkSecurity' || key === 'network-security') return 'networkSecurity'
      if (key === 'incidentResponse' || key === 'incident-response') return 'incidentResponse'
      if (key === 'training') return 'training'
      if (key === 'compliance') return 'compliance'
      return 'training'
    }
    try {
      (dashboardData?.tasks || []).forEach(t => {
        const status = (t.status || '').toLowerCase()
        const isInProgress = (status === 'in_progress' || status === 'in-progress')
        if (isInProgress) {
          const n = normalize(t.category)
          if (counts[n] !== undefined) counts[n]++
        }
      })
    } catch (e) {
      console.warn('Error computing inProgressAssignedByCategory:', e)
    }
    return counts
  }, [dashboardData?.tasks])

  // Fallback: preview lacking items per category from latest assessment (before tasks are assigned)
  const previewLackingByCategory = useMemo(() => {
    const counts = {
      dataProtection: 0,
      accessControl: 0,
      networkSecurity: 0,
      incidentResponse: 0,
      training: 0,
      compliance: 0
    }
    const normalize = (cat) => String(cat || '').toLowerCase().replace(/\s+/g, '')
    try {
      (dashboardData?.compliancePreview || []).forEach(p => {
        const n = normalize(p.category)
        if (counts[n] !== undefined) counts[n]++
      })
    } catch (e) {
      console.warn('Error computing previewLackingByCategory:', e)
    }
    return counts
  }, [dashboardData?.compliancePreview])

  // Effective lacking count: prefer assigned incomplete, else fall back to preview minus completed
  const effectiveLackingByCategory = useMemo(() => {
    const keys = ['dataProtection','accessControl','networkSecurity','incidentResponse','training','compliance']
    const result = {}
    keys.forEach(k => {
      const assignedIncomplete = incompleteAssignedByCategory[k] || 0
      if (assignedIncomplete > 0) {
        result[k] = assignedIncomplete
      } else {
        const previewCount = previewLackingByCategory[k] || 0
        const completedCount = completedAssignedByCategory[k] || 0
        result[k] = Math.max(previewCount - completedCount, 0)
      }
    })
    return result
  }, [incompleteAssignedByCategory, previewLackingByCategory, completedAssignedByCategory])

  // Determine categories that have compliance work (preview or assigned tasks exist)
  const hasComplianceWorkByCategory = useMemo(() => {
    const keys = ['dataProtection','accessControl','networkSecurity','incidentResponse','training','compliance']
    const result = {}
    keys.forEach(k => {
      const hasPreview = (previewLackingByCategory[k] || 0) > 0
      const hasAssignedIncomplete = (incompleteAssignedByCategory[k] || 0) > 0
      const hasAssignedCompleted = (completedAssignedByCategory[k] || 0) > 0
      const hasInProgress = (inProgressAssignedByCategory[k] || 0) > 0
      result[k] = hasPreview || hasAssignedIncomplete || hasAssignedCompleted || hasInProgress
    })
    return result
  }, [previewLackingByCategory, incompleteAssignedByCategory, completedAssignedByCategory, inProgressAssignedByCategory])

  const categoryData = [
    { name: 'Data Protection', key: 'dataProtection', icon: FileText, score: dashboardData.categories.dataProtection?.score || 0, total: dashboardData.categories.dataProtection?.total || 1 },
    { name: 'Access Control', key: 'accessControl', icon: Lock, score: dashboardData.categories.accessControl?.score || 0, total: dashboardData.categories.accessControl?.total || 1 },
    { name: 'Network Security', key: 'networkSecurity', icon: Wifi, score: dashboardData.categories.networkSecurity?.score || 0, total: dashboardData.categories.networkSecurity?.total || 1 },
    { name: 'Incident Response', key: 'incidentResponse', icon: AlertTriangle, score: dashboardData.categories.incidentResponse?.score || 0, total: dashboardData.categories.incidentResponse?.total || 1 },
    { name: 'Training', key: 'training', icon: Users, score: dashboardData.categories.training?.score || 0, total: dashboardData.categories.training?.total || 1 },
    { name: 'Compliance', key: 'compliance', icon: Shield, score: dashboardData.categories.compliance?.score || 0, total: dashboardData.categories.compliance?.total || 1 }
  ]



  // Helper function to load tasks from database
  const loadUpcomingTasks = async () => {
    console.log('🔍 loadUpcomingTasks called, user:', user?.id)
    
    if (!user) {
      console.log('❌ No user found, using fallback data')
      // Fallback to static data if no user
      setUpcomingTasks([
        { id: 1, title: 'Quarterly Security Training', dueDate: '2024-02-15', priority: 'high', category: 'training' },
        { id: 2, title: 'Password Policy Review', dueDate: '2024-02-20', priority: 'medium', category: 'accessControl' },
        { id: 3, title: 'Backup System Test', dueDate: '2024-02-25', priority: 'high', category: 'dataProtection' },
        { id: 4, title: 'Network Security Audit', dueDate: '2024-03-01', priority: 'medium', category: 'networkSecurity' }
      ])
      return
    }

    try {
      console.log('📡 Fetching tasks from database for user:', user.id)
      
      // First, let's check all tasks for this user
      const { data: allTasks, error: allTasksError } = await supabase
        .from('tasks')
        .select('*')
        .or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`)
      
      console.log('📊 All tasks for user:', allTasks)
      console.log('📊 All tasks error:', allTasksError)
      
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`)
        .order('due_date', { ascending: true })
        .limit(10)

      console.log('📋 Tasks query result:', { data, error })
      console.log('📋 Number of tasks found:', data?.length || 0)

      if (error) {
        console.error('❌ Error loading upcoming tasks:', error)
        // Fallback to static data
        setUpcomingTasks([
          { id: 1, title: 'Quarterly Security Training', dueDate: '2024-02-15', priority: 'high', category: 'training' },
          { id: 2, title: 'Password Policy Review', dueDate: '2024-02-20', priority: 'medium', category: 'accessControl' },
          { id: 3, title: 'Backup System Test', dueDate: '2024-02-25', priority: 'high', category: 'dataProtection' },
          { id: 4, title: 'Network Security Audit', dueDate: '2024-03-01', priority: 'medium', category: 'networkSecurity' }
        ])
      } else if (data && data.length > 0) {
        console.log('✅ Found tasks, formatting for display:', data)
        // Format tasks for display
        const formattedTasks = data.map(task => ({
          id: task.id,
          title: task.title,
          dueDate: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
          priority: task.priority === 1 ? 'low' : task.priority === 2 ? 'medium' : task.priority === 3 ? 'high' : task.priority === 4 ? 'critical' : 'medium',
          category: task.category || 'training',
          status: task.status || 'pending'
        }))
        console.log('🎯 Formatted tasks:', formattedTasks)
        setUpcomingTasks(formattedTasks)
      } else {
        console.log('📭 No tasks found, setting empty array')
        // No tasks found, show welcome message
        setUpcomingTasks([])
      }
    } catch (error) {
      console.error('💥 Exception in loadUpcomingTasks:', error)
      // Fallback to static data
      setUpcomingTasks([
        { id: 1, title: 'Quarterly Security Training', dueDate: '2024-02-15', priority: 'high', category: 'training' },
        { id: 2, title: 'Password Policy Review', dueDate: '2024-02-20', priority: 'medium', category: 'accessControl' },
        { id: 3, title: 'Backup System Test', dueDate: '2024-02-25', priority: 'high', category: 'dataProtection' },
        { id: 4, title: 'Network Security Audit', dueDate: '2024-03-01', priority: 'medium', category: 'networkSecurity' }
      ])
    }
  }

  // Helper function to load activities from localStorage
  const loadActivities = () => {
    try {
      const saved = localStorage.getItem('recentActivities')
      if (saved) {
        setRecentActivities(JSON.parse(saved))
      } else {
        // Fallback to static data if no activities exist
        setRecentActivities([
          { id: 1, action: 'Welcome to CyberGov! Start by creating your first task.', timestamp: new Date().toISOString(), type: 'system' }
        ])
      }
    } catch (error) {
      console.error('Error loading activities from localStorage:', error)
      setRecentActivities([
        { id: 1, action: 'Welcome to CyberGov! Start by creating your first task.', timestamp: new Date().toISOString(), type: 'system' }
      ])
    }
  }

  // Helper function to format activity timestamp
  const formatActivityTime = (timestamp) => {
    const now = new Date()
    const activityTime = new Date(timestamp)
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours} hours ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays} days ago`
    
    return activityTime.toLocaleDateString()
  }

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-success-600'
    if (percentage >= 60) return 'text-warning-600'
    return 'text-security-600'
  }

  const getScoreBgColor = (percentage) => {
    if (percentage >= 80) return 'bg-success-500'
    if (percentage >= 60) return 'bg-warning-500'
    return 'bg-security-500'
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-security-100 text-security-700 border-security-200'
      case 'medium': return 'bg-warning-100 text-warning-700 border-warning-200'
      case 'low': return 'bg-success-100 text-success-700 border-success-200'
      default: return 'bg-gov-100 text-gov-700 border-gov-200'
    }
  }

  // Status badge colors for tasks - using same normalization as renderStatusBadge
  const getStatusColor = (status) => {
    const s = normalizeStatus(status)
    if (s === 'completed') return 'bg-green-100 text-green-700 border-green-200'
    if (s === 'in_progress') return 'bg-yellow-100 text-yellow-700 border-yellow-200'
    if (s === 'overdue') return 'bg-red-100 text-red-700 border-red-200'
    return 'bg-gray-100 text-gray-700 border-gray-200'
  }

  // Format status labels consistently
  const formatStatusLabel = (status) => {
    const s = normalizeStatus(status)
    if (s === 'completed') return 'Completed'
    if (s === 'in_progress') return 'In Progress'
    if (s === 'overdue') return 'Overdue'
    return 'Pending'
  }

  // Analytics utility functions
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <ArrowUp className="w-4 h-4 text-success-600" />
      case 'down': return <ArrowDown className="w-4 h-4 text-security-600" />
      default: return <Minus className="w-4 h-4 text-gov-600" />
    }
  }

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up': return 'text-success-600'
      case 'down': return 'text-security-600'
      default: return 'text-gov-600'
    }
  }

  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return 0
    return ((current - previous) / previous * 100).toFixed(1)
  }

  const getMetricDisplayName = (metric) => {
    const names = {
      security_score: 'Security Score',
      incidents: 'Security Incidents',
      compliance: 'Compliance Rate',
      training: 'Training Completion'
    }
    return names[metric] || metric
  }

  const renderMiniChart = (data, color = '#3B82F6') => {
    const max = Math.max(...data)
    const min = Math.min(...data)
    const range = max - min || 1
    
    return (
      <svg className="w-20 h-8" viewBox="0 0 80 32">
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          points={data.map((value, index) => {
            const x = (index / (data.length - 1)) * 80
            const y = 32 - ((value - min) / range) * 32
            return `${x},${y}`
          }).join(' ')}
        />
      </svg>
    )
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-900 via-gov-800 to-cyber-900 py-8 relative overflow-hidden">
      {/* Cybersecurity Grid Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(14, 165, 233, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>
      
      {/* Floating Security Elements */}
      <div className="absolute top-20 left-10 w-2 h-2 bg-cyber-400 rounded-full animate-pulse opacity-60" />
      <div className="absolute top-40 right-20 w-1 h-1 bg-security-400 rounded-full animate-ping opacity-40" />
      <div className="absolute bottom-32 left-1/4 w-1.5 h-1.5 bg-cyber-300 rounded-full animate-pulse opacity-50" />
      
      <div className="relative max-w-full mx-auto px-6 sm:px-8 lg:px-12 xl:px-16">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-r from-cyber-600/20 to-security-600/20 backdrop-blur-sm border border-cyber-500/30 rounded-2xl p-6 shadow-2xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyber-500 to-cyber-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-display font-bold bg-gradient-to-r from-white to-cyber-200 bg-clip-text text-transparent mb-2">
                    CyberGov Dashboard
                  </h1>
                  <p className="text-cyber-200/80 flex items-center space-x-2">
                    <Activity className="w-4 h-4" />
                    <span>Welcome back, {profile?.name || user?.email || getUserTypeLabel()}</span>
                    {profile?.agency && <span className="text-cyber-300/60">• {profile.agency}</span>}
                  </p>
                </div>
              </div>
              <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-4 lg:space-y-0 lg:space-x-6 mt-4 md:mt-0 w-full lg:w-auto">
                {/* Analytics View Selector */}
                <div className="flex items-center space-x-3 min-w-0">
                  <Filter className="w-4 h-4 text-cyber-300" />
                  <select 
                    value={analyticsView} 
                    onChange={(e) => setAnalyticsView(e.target.value)}
                    className="text-sm bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white backdrop-blur-sm focus:ring-2 focus:ring-cyber-400 focus:border-transparent transition-all duration-200"
                  >
                    <option value="overview" className="text-gray-900">Overview</option>
                    <option value="detailed" className="text-gray-900">Detailed Analytics</option>
                    <option value="trends" className="text-gray-900">Trend Analysis</option>
                    <option value="comparison" className="text-gray-900">Benchmarking</option>
                  </select>
                </div>
                
                {/* Timeframe Selector */}
                <div className="flex items-center space-x-3 min-w-0">
                  <Clock className="w-4 h-4 text-cyber-300" />
                  <select 
                    value={selectedTimeframe} 
                    onChange={(e) => setSelectedTimeframe(e.target.value)}
                    className="text-sm bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white backdrop-blur-sm focus:ring-2 focus:ring-cyber-400 focus:border-transparent transition-all duration-200"
                  >
                    <option value="1month" className="text-gray-900">Last Month</option>
                    <option value="3months" className="text-gray-900">Last 3 Months</option>
                    <option value="6months" className="text-gray-900">Last 6 Months</option>
                    <option value="1year" className="text-gray-900">Last Year</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-3 flex-wrap lg:flex-nowrap gap-2 lg:gap-0">
                  <button 
                    onClick={handleRefresh}
                    disabled={loading}
                    className="bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white border border-white/20 px-4 py-2.5 rounded-xl flex items-center justify-center space-x-2 transition-all duration-200 backdrop-blur-sm whitespace-nowrap font-medium min-w-fit hover:shadow-lg"
                  >
                    <RefreshCw size={16} className={`flex-shrink-0 ${loading ? 'animate-spin' : ''}`} />
                    <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
                  </button>

                  {/* Only show Assessment link for non-admin users */}
                  {!(profile?.user_type === 'it_administrator' || profile?.user_type === 'security_officer' || profile?.role === 'Administrator') && (
                    <Link
                      to="/assessment"
                      className="bg-gradient-to-r from-cyber-500 to-cyber-600 hover:from-cyber-600 hover:to-cyber-700 text-white px-5 py-2.5 rounded-xl flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-cyber-500/25 whitespace-nowrap font-medium min-w-fit"
                    >
                      <RefreshCw size={16} className="flex-shrink-0" />
                      <span>New Assessment</span>
                    </Link>
                  )}
                  
                  <button 
                    onClick={handleExportReport}
                    className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-5 py-2.5 rounded-xl flex items-center space-x-2 transition-all duration-200 backdrop-blur-sm whitespace-nowrap font-medium min-w-fit hover:shadow-lg"
                  >
                    <Download size={16} className="flex-shrink-0" />
                    <span>Export Report</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Dynamic Filter Summary */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="bg-gradient-to-r from-cyber-500/10 to-security-500/10 backdrop-blur-sm border border-cyber-400/30 rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-cyber-400 rounded-full animate-pulse shadow-lg shadow-cyber-400/50"></div>
                  <span className="text-sm font-medium text-white">{getViewSummary().title}</span>
                </div>
                <div className="text-sm text-cyber-200/80">{getViewSummary().description}</div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-cyber-300 font-medium">{getViewSummary().highlight}</div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs px-3 py-1.5 bg-cyber-500/30 text-cyber-200 rounded-full border border-cyber-400/30 backdrop-blur-sm">
                    {analyticsView.charAt(0).toUpperCase() + analyticsView.slice(1)}
                  </span>
                  <span className="text-xs px-3 py-1.5 bg-white/10 text-white rounded-full border border-white/20 backdrop-blur-sm">
                    {selectedTimeframe.replace(/([0-9]+)/, '$1 ').replace('months', 'mo').replace('month', 'mo').replace('year', 'yr')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Top Widgets Row */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
        >
          {/* Security Score Trend Widget - Removed for simplified view */}

          {/* Risk Summary Widget - Hide for IT Administrator */}
          {!(profile?.user_type === 'it_administrator' || profile?.user_type === 'security_officer' || profile?.role === 'Administrator') && (
          <motion.div variants={itemVariants} className="md:col-span-2 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-warning-400" />
                <span>Risk Summary</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full border ${
                dashboardData.riskLevel === 'low' ? 'bg-success-500/20 text-success-300 border-success-400/30' :
                dashboardData.riskLevel === 'medium' ? 'bg-warning-500/20 text-warning-300 border-warning-400/30' :
                'bg-red-500/20 text-red-300 border-red-400/30'
              }`}>
                {dashboardData.riskLevel?.toUpperCase()}
              </span>
            </h2>
            {dashboardData.recommendations && dashboardData.recommendations.length > 0 ? (
              <div className="space-y-3">
                {(() => {
                  // Calculate risk distribution from recommendations
                  const riskCounts = { critical: 0, high: 0, medium: 0, low: 0 }
                  dashboardData.recommendations.forEach(rec => {
                    const priority = rec.priority || 'medium'
                    if (riskCounts[priority] !== undefined) {
                      riskCounts[priority]++
                    }
                  })
                  
                  const totalRisks = Object.values(riskCounts).reduce((sum, count) => sum + count, 0)
                  const riskData = [
                    { name: 'Critical', count: riskCounts.critical, color: '#DC2626', percentage: totalRisks > 0 ? Math.round((riskCounts.critical / totalRisks) * 100) : 0 },
                    { name: 'High', count: riskCounts.high, color: '#EA580C', percentage: totalRisks > 0 ? Math.round((riskCounts.high / totalRisks) * 100) : 0 },
                    { name: 'Medium', count: riskCounts.medium, color: '#D97706', percentage: totalRisks > 0 ? Math.round((riskCounts.medium / totalRisks) * 100) : 0 },
                    { name: 'Low', count: riskCounts.low, color: '#16A34A', percentage: totalRisks > 0 ? Math.round((riskCounts.low / totalRisks) * 100) : 0 }
                  ].filter(risk => risk.count > 0)
                  
                  return (
                    <>
                      {riskData.length > 0 ? riskData.map((risk) => (
                        <div key={risk.name} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10">
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-3 h-3 rounded-full shadow-lg" 
                              style={{ backgroundColor: risk.color, boxShadow: `0 0 10px ${risk.color}50` }}
                            />
                            <span className="text-sm text-white font-medium">{risk.name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-cyber-300 bg-cyber-500/20 px-2 py-1 rounded">{risk.count}</span>
                            <span className="text-sm font-bold text-cyber-300">{risk.percentage}%</span>
                          </div>
                        </div>
                      )) : (
                        <div className="flex items-center justify-center py-4">
                          <div className="text-center bg-success-500/10 border border-success-400/30 rounded-xl p-4">
                            <CheckCircle className="w-8 h-8 text-success-400 mx-auto mb-2" />
                            <span className="text-sm text-success-300 font-medium">No Active Risks</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-2 border-t border-white/20">
                        <div className="flex items-center justify-between text-sm bg-warning-500/10 border border-warning-400/30 rounded-xl p-3">
                          <span className="text-warning-300">Total Issues</span>
                          <span className="font-bold text-warning-400">{totalRisks}</span>
                        </div>
                      </div>
                    </>
                  )
                })()}
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="bg-cyber-500/10 border border-cyber-400/30 rounded-xl p-6">
                  <Shield className="w-12 h-12 text-cyber-400 mx-auto mb-3" />
                  <p className="text-sm text-cyber-200/70">Complete an assessment to see risk analysis</p>
                </div>
              </div>
            )}
          </motion.div>
          )}
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overall Score Card - Hide for IT Administrator */}
            {!(profile?.user_type === 'it_administrator' || profile?.user_type === 'security_officer' || profile?.role === 'Administrator') && (
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                  <Target className="w-6 h-6 text-cyber-400" />
                  <span>Overall Security Score</span>
                </h2>
                {dashboardData.lastAssessmentDate && (
                  <span className="text-sm text-cyber-200/70 bg-cyber-500/20 px-3 py-1 rounded-lg">
                    Last updated: {new Date(dashboardData.lastAssessmentDate).toLocaleDateString()}
                  </span>
                )}
              </div>
              
              {dashboardData.lastAssessmentDate ? (
                <div className="flex items-center space-x-6">
                  <div className="flex-shrink-0">
                    <div className="relative w-24 h-24">
                      <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="transparent"
                          className="text-white/20"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="transparent"
                          strokeDasharray={`${2 * Math.PI * 40}`}
                          strokeDashoffset={`${2 * Math.PI * 40 * (1 - dashboardData.score / 100)}`}
                          className={getScoreColor(dashboardData.score)}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className={`text-2xl font-bold ${getScoreColor(dashboardData.score)}`}>
                          {Math.round(dashboardData.score)}%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-3xl">{getRiskLevelIcon()}</span>
                      <span className={`text-xl font-semibold capitalize ${getRiskLevelColor()}`}>
                        {dashboardData.riskLevel} Risk Level
                      </span>
                    </div>
                    <p className="text-cyber-200/80 mb-4">
                      {dashboardData.riskLevel === 'low' && 'Excellent security posture! Keep up the good work.'}
                      {dashboardData.riskLevel === 'medium' && 'Good progress with room for improvement.'}
                      {dashboardData.riskLevel === 'high' && 'Immediate attention required for critical security gaps.'}
                    </p>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1 text-sm text-success-400 bg-success-500/10 px-3 py-1.5 rounded-lg border border-success-400/30">
                        <TrendingUp size={16} />
                        <span>+12% from last month</span>
                      </div>
                      <div className="flex items-center space-x-1 text-sm text-cyber-300 bg-cyber-500/10 px-3 py-1.5 rounded-lg border border-cyber-400/30">
                        <Target size={16} />
                        <span>Goal: 90%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-center py-8">
                  <div className="bg-cyber-500/10 border border-cyber-400/30 rounded-xl p-8">
                    <BarChart3 className="w-16 h-16 text-cyber-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No Assessment Data</h3>
                    <p className="text-cyber-200/70 mb-4">Take your first cybersecurity assessment to see your score.</p>
                    <Link to="/assessment" className="bg-gradient-to-r from-cyber-500 to-cyber-600 hover:from-cyber-600 hover:to-cyber-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-cyber-500/25">
                      Start Assessment
                    </Link>
                  </div>
                </div>
                </div>
              )}
            </motion.div>
            )}

            {/* Category Breakdown - Hide for IT Administrator */}
            {!(profile?.user_type === 'it_administrator' || profile?.user_type === 'security_officer' || profile?.role === 'Administrator') && (
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <Grid className="w-6 h-6 text-cyber-400" />
                <span>Security Categories</span>
                {Object.values(inProgressAssignedByCategory || {}).reduce((a, b) => a + b, 0) > 0 ? (
                  <span className="inline-flex items-center text-xs font-semibold text-amber-300 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-400/30 ml-3">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    In progress
                  </span>
                ) : (
                  (() => {
                    const keys = ['dataProtection','accessControl','networkSecurity','incidentResponse','training','compliance']
                    const hasAnyWork = keys.some(k => hasComplianceWorkByCategory[k])
                    const allWithWorkComplied = keys
                      .filter(k => hasComplianceWorkByCategory[k])
                      .every(k => (effectiveLackingByCategory[k] || 0) === 0 && (inProgressAssignedByCategory[k] || 0) === 0)
                    return hasAnyWork && allWithWorkComplied
                  })() && (
                    <span className="inline-flex items-center text-xs font-semibold text-green-300 bg-green-500/10 px-2.5 py-0.5 rounded-full border border-green-400/30 ml-3">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" />
                      Complied
                    </span>
                  )
                )}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {categoryData.map((category) => {
                  const Icon = category.icon
                  const percentage = category.score || 0  // score is already a percentage
                  const correctAnswers = Math.round((percentage / 100) * category.total)
                  
                  return (
                    <div key={category.key} className="bg-white/5 border border-white/20 rounded-xl p-4 hover:bg-white/10 transition-all duration-300">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-8 h-8 bg-cyber-500/20 rounded-lg flex items-center justify-center border border-cyber-400/30">
                          <Icon className="w-4 h-4 text-cyber-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-white">{category.name}</h3>
                          <p className="text-sm text-cyber-200/70">
                            {correctAnswers}/{category.total} completed • Compliance tasks since last assessment: {effectiveLackingByCategory[category.key] || 0} lacking to comply
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`text-lg font-semibold ${getScoreColor(percentage)}`}>
                            {Math.round(percentage)}%
                          </span>
                          {hasComplianceWorkByCategory[category.key] && (effectiveLackingByCategory[category.key] || 0) === 0 && (inProgressAssignedByCategory[category.key] || 0) === 0 && (
                            <span className="inline-flex items-center text-xs font-semibold text-green-400 bg-green-500/10 px-2.5 py-0.5 rounded-full border border-green-400/30">
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              Complied
                            </span>
                          )}
                          {(inProgressAssignedByCategory[category.key] || 0) > 0 && (
                            <span className="inline-flex items-center text-xs font-semibold text-amber-400 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-400/30">
                              <RefreshCw className="w-3.5 h-3.5 mr-1" />
                              In progress
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-500 ${getScoreBgColor(percentage)}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </motion.div>
            )}

            {/* AI Security Analysis */}
            {!(profile?.user_type === 'it_administrator' || 
              profile?.user_type === 'security_officer' ||
              profile?.role === 'Administrator') && (
            <motion.div variants={itemVariants}>
              <AISummary 
                assessmentData={dashboardData}
                onRecommendationClick={(recommendation) => {
                  // Handle recommendation click - could open a modal or navigate
                  console.log('Recommendation clicked:', recommendation)
                }}
              />
            </motion.div>
            )}

            {/* Agency Risk Scores section will show for admin users */}

            {/* Individual Assessments with Agency Names - Admin Only */}
            {(profile?.user_type === 'it_administrator' || 
              profile?.user_type === 'security_officer' ||
              profile?.role === 'Administrator') && (
              <motion.div variants={itemVariants} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <FileText className="w-6 h-6 text-cyber-400" />
                    <span>Individual Assessments by Agency</span>
                  </h2>
                  <div className="flex items-center space-x-2 text-sm text-cyber-200/70 bg-cyber-500/20 px-3 py-1.5 rounded-lg border border-cyber-400/30">
                    <Shield className="w-4 h-4" />
                    <span>Completed cybersecurity assessments across all agencies</span>
                  </div>
                </div>

                {/* Summary Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <FileText className="w-5 h-5 text-blue-400" />
                      <span className="text-sm font-medium text-cyber-200/80">Total Assessments</span>
                    </div>
                    <span className="text-2xl font-bold text-white">{agencyRiskScores.length}</span>
                  </div>
                  <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-sm font-medium text-cyber-200/80">Low Risk</span>
                    </div>
                    <span className="text-2xl font-bold text-green-400">
                      {agencyRiskScores.filter(assessment => assessment.riskLevel === 'low').length}
                    </span>
                  </div>
                  <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      <span className="text-sm font-medium text-cyber-200/80">Medium Risk</span>
                    </div>
                    <span className="text-2xl font-bold text-yellow-400">
                      {agencyRiskScores.filter(assessment => assessment.riskLevel === 'medium').length}
                    </span>
                  </div>
                  <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Shield className="w-5 h-5 text-red-400" />
                      <span className="text-sm font-medium text-cyber-200/80">High Risk</span>
                    </div>
                    <span className="text-2xl font-bold text-red-400">
                      {agencyRiskScores.filter(assessment => assessment.riskLevel === 'high').length}
                    </span>
                  </div>
                </div>

                {/* Individual Assessments List */}
                <div className="space-y-4">
                  {agencyRiskScores.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-cyber-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">No Assessments Available</h3>
                      <p className="text-cyber-200/70">No completed assessments found in the database.</p>
                    </div>
                  ) : (
                    agencyRiskScores.map((assessment, index) => {
                      const riskColor = assessment.riskLevel === 'low' ? 'text-green-400' : 
                                       assessment.riskLevel === 'medium' ? 'text-yellow-400' : 'text-red-400'
                      const riskBgColor = assessment.riskLevel === 'low' ? 'bg-green-400/20 border-green-400/30' : 
                                         assessment.riskLevel === 'medium' ? 'bg-yellow-400/20 border-yellow-400/30' : 'bg-red-400/20 border-red-400/30'
                      const riskIcon = assessment.riskLevel === 'low' ? CheckCircle : 
                                       assessment.riskLevel === 'medium' ? AlertTriangle : Shield
                      const RiskIcon = riskIcon

                      return (
                        <motion.div
                          key={assessment.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="bg-white/5 border border-white/20 rounded-xl p-4 hover:bg-white/10 transition-all duration-300"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${riskBgColor}`}>
                                <RiskIcon className={`w-5 h-5 ${riskColor}`} />
                              </div>
                              <div>
                                <h3 className="font-semibold text-white">{assessment.agency}</h3>
                                <p className="text-sm text-cyber-200/70">{assessment.title}</p>
                                <p className="text-xs text-cyber-300/60">
                                  By: {assessment.userInfo.name} ({assessment.userInfo.position || 'N/A'})
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-2xl font-bold ${riskColor}`}>
                                {assessment.score}%
                              </div>
                              <div className={`text-sm font-medium ${riskColor} capitalize`}>
                                {assessment.riskLevel} Risk
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            <div className="text-center">
                              <div className="text-sm text-cyber-200/70 mb-1">Completed</div>
                              <div className="text-sm font-semibold text-white">
                                {new Date(assessment.completedAt).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm text-cyber-200/70 mb-1">Status</div>
                              <div className="text-sm font-semibold text-green-400 capitalize">{assessment.status}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm text-cyber-200/70 mb-1">User ID</div>
                              <div className="text-sm font-semibold text-cyber-300">{assessment.userId}</div>
                            </div>
                          </div>
                          
                          <div className="w-full bg-white/20 rounded-full h-3 mb-3">
                            <div 
                              className={`h-3 rounded-full transition-all duration-500 ${
                                assessment.riskLevel === 'low' ? 'bg-gradient-to-r from-green-500 to-green-400' : 
                                assessment.riskLevel === 'medium' ? 'bg-gradient-to-r from-yellow-500 to-yellow-400' : 
                                'bg-gradient-to-r from-red-500 to-red-400'
                              }`}
                              style={{ width: `${assessment.score}%` }}
                            />
                          </div>

                          {assessment.description && (
                            <div className="mb-3 p-3 bg-white/5 rounded-lg border border-white/10">
                              <p className="text-sm text-cyber-200/80">{assessment.description}</p>
                            </div>
                          )}
                          
                          <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-2 text-sm text-cyber-200/70">
                              <Clock className="w-4 h-4" />
                              <span>
                                Completed {new Date(assessment.completedAt).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewAssessments(assessment.agency)}
                                className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                              >
                                <FileText className="w-4 h-4" />
                                <span>View Details</span>
                              </button>

                              {(Array.isArray(noResponseMap[assessment.id]) && noResponseMap[assessment.id].length > 0) && (
                                <button
                                  onClick={() => {
                                    console.log('🎯 Schedule button clicked - Non-compliant answers present:', {
                                      id: assessment.id,
                                      nonCompliantCount: noResponseMap[assessment.id].length
                                    })
                                    handleScheduleReminders(assessment)
                                  }}
                                  className="flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-all duration-300 bg-purple-500 hover:bg-purple-600 text-white"
                                  title="Map failing responses to scheduled reminders"
                                >
                                  <Clock className="w-4 h-4" />
                                  <span>Schedule Task</span>
                                </button>
                              )}
                            </div>
                          </div>
                          {/* Non-compliant answers (response_value = "No") */}
                          <div className="mt-4 border-t border-white/10 pt-3">
                            <div className="text-sm text-cyber-200/70 mb-2">Non-compliant answers</div>
                            {noResponseMap[assessment.id] === null ? (
                              <div className="text-xs text-cyber-300">Unable to load responses due to access restrictions.</div>
                            ) : Array.isArray(noResponseMap[assessment.id]) && noResponseMap[assessment.id].length > 0 ? (
                              <ul className="space-y-1">
                                {noResponseMap[assessment.id].map((item, idx) => (
                                  <li
                                    key={`${assessment.id}-${item.question_id}-${idx}`}
                                    onClick={() => openResponseModal(assessment, item)}
                                    className="cursor-pointer text-sm text-white/90 flex justify-between items-center bg-white/5 border border-white/10 rounded-md px-3 py-2 hover:bg-white/10 transition-colors"
                                  >
                                    <span className="flex-1">{item.question_text}</span>
                                    {item.category && <span className="text-xs text-cyber-300 ml-2">{item.category}</span>}
                                    <span className="ml-4 px-2 py-0.5 text-xs rounded bg-red-500/20 border border-red-400/30 text-red-400">No</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-xs text-cyber-300 rounded px-2 py-2 items-center rounded bg-green-500/20 border border-green-400/30 text-green-400">Compliance to All Identified Risk </div>
                            )}
                          </div>
                        </motion.div>
                      )
                    })
                  )}
                </div>
              </motion.div>
            )}

{/* Modal View */}
            {/* Assign Compliance Modal */}
            {showAssignModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gov-800">Assign Compliance Tasks</h3>
                    <button onClick={closeAssignModal} className="p-2 hover:bg-gov-100 rounded-md">
                      <X className="w-5 h-5 text-gov-600" />
                    </button>
                  </div>
                  <p className="text-sm text-gov-600 mb-4">
                    {assignAllCompleted
                      ? 'All items are completed. You can review them below.'
                      : 'Review lacking items below, then choose dates for assignment.'}
                  </p>
                  <div className="mb-4 border border-gov-200 rounded-lg">
                    <div className="px-3 py-2 border-b border-gov-200 flex items-center justify-between">
                      <span className="text-sm font-medium text-gov-800">
                        {assignAllCompleted ? 'Complied items' : 'Lacking items to comply'}
                      </span>
                      <span className="text-xs text-gov-600">
                        {assignPreviewLoading ? 'Loading…' : (() => {
                          const included = assignPreviewTasks.filter(t => t.include !== false)
                          const countFor = (t) => {
                            const qids = Array.isArray(t?.question_ids) ? t.question_ids : []
                            return qids.length > 0 ? qids.length : 1
                          }
                          const totalItems = included.reduce((sum, t) => sum + countFor(t), 0)
                          const cPending = included.filter(t => (normalizeStatus(t.status) || 'pending') === 'pending').reduce((s, t) => s + countFor(t), 0)
                          const cScheduled = included.filter(t => (normalizeStatus(t.status) || '') === 'scheduled').reduce((s, t) => s + countFor(t), 0)
                          const cInProgress = included.filter(t => (normalizeStatus(t.status) || 'pending') === 'in_progress').reduce((s, t) => s + countFor(t), 0)
                          const cCompleted = included.filter(t => (normalizeStatus(t.status) || 'pending') === 'completed').reduce((s, t) => s + countFor(t), 0)
                          return `${totalItems} items • Pending ${cPending} • Scheduled ${cScheduled} • In Progress ${cInProgress} • Completed ${cCompleted}`
                        })()}
                      </span>
                    </div>
                    <div className="max-h-48 overflow-auto">
                      {(() => {
                        if (assignPreviewLoading) {
                          return <div className="p-3 text-sm text-gov-600">Fetching assessment details…</div>;
                        }
                        if (assignPreviewTasks.length === 0) {
                          return <div className="p-3 text-sm text-gov-600">No specific gaps detected. You can still assign a general improvement task.</div>;
                        }
                        return (
                          <div>
                            <div className="px-3 py-1 text-xs flex justify-between text-gov-500">
                              <span>Item</span>
                              <span>Status</span>
                            </div>
                            {assignPreviewTasks.map((t, idx) => (
                              <div key={idx} className="px-3 py-2 border-t border-gov-100">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="text-sm font-medium text-gov-800">{String(t.title).replace(/^\[AG:[^\]]+\]\s*/,'').replace(/^\[AID:[^\]]+\]\s*/,'')}</div>
                                  {renderStatusBadge(t.status || 'pending')}
                                </div>
                                {t.description && (
                              <div className="text-xs text-gov-600 mt-0.5">{t.description}</div>
                            )}
                            {(t.due_date || assignDates?.dueDate) && (
                              <div className="text-xs text-gov-500 mt-0.5">Due {t.due_date || assignDates?.dueDate}</div>
                            )}
                            <div className="text-xs text-gov-600 mt-0.5 flex items-center gap-1">
                              <span className="text-gov-500">Status:</span>
                              {renderStatusBadge(t.status || 'pending')}
                            </div>
                            {!assignAllCompleted && (
                              <div className="mt-2 flex items-center gap-4">
                                    <label className="inline-flex items-center gap-2 text-xs text-gov-700">
                                      <input
                                        type="radio"
                                        name={`assignee-${idx}`}
                                        value="owner"
                                        checked={t.assignee === 'owner'}
                                        onChange={(e) => {
                                          const val = e.target.value
                                          setAssignPreviewTasks(prev => prev.map((item, i) => i === idx ? { ...item, assignee: val } : item))
                                        }}
                                      />
                                      <span>Assign to {selectedAssessmentForAssign?.userInfo?.name || 'Assessment Owner'}</span>
                                    </label>
                                    <label className="inline-flex items-center gap-2 text-xs text-gov-700">
                                      <input
                                        type="radio"
                                        name={`assignee-${idx}`}
                                        value="me"
                                        checked={t.assignee === 'me'}
                                        onChange={(e) => {
                                          const val = e.target.value
                                          setAssignPreviewTasks(prev => prev.map((item, i) => i === idx ? { ...item, assignee: val } : item))
                                        }}
                                      />
                                      <span>Assign to me</span>
                                    </label>
                                    <label className="inline-flex items-center gap-2 text-xs text-gov-700 ml-auto">
                                      <input
                                        type="checkbox"
                                        checked={t.include !== false}
                                        onChange={(e) => {
                                          const checked = e.target.checked
                                          setAssignPreviewTasks(prev => prev.map((item, i) => i === idx ? { ...item, include: checked } : item))
                                        }}
                                      />
                                      <span>Include</span>
                                    </label>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  {!assignAllCompleted && !assignJustCreated && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gov-700 mb-2">Start Date</label>
                        <input
                          type="date"
                          value={assignDates.startDate}
                          onChange={(e) => setAssignDates(prev => ({ ...prev, startDate: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent border-gov-300"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gov-700 mb-2">Due Date</label>
                        <input
                          type="date"
                          value={assignDates.dueDate}
                          onChange={(e) => setAssignDates(prev => ({ ...prev, dueDate: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent border-gov-300"
                        />
                        {assignDates.startDate && assignDates.dueDate && new Date(assignDates.dueDate) < new Date(assignDates.startDate) && (
                          <p className="text-red-600 text-sm mt-1">Due date must be after start date.</p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="mt-6 flex justify-end gap-3">
                    <button onClick={closeAssignModal} className="px-4 py-2 border border-gov-300 rounded-lg text-gov-700 hover:bg-gov-100">Cancel</button>
                    {assignError && (
                      <p className="text-sm text-red-600 self-center mr-auto">{assignError}</p>
                    )}
                    {assignAllCompleted ? (
                      <button
                        onClick={handleViewCompliedTasks}
                        className="px-4 py-2 rounded-lg text-white bg-cyber-600 hover:bg-cyber-700"
                      >
                        View Complied Tasks
                      </button>
                    ) : assignJustCreated ? (
                      <span className="px-4 py-2 rounded-lg">
                        {renderStatusBadge('pending')}
                      </span>
                    ) : (
                      <button
                        onClick={handleConfirmAssignCompliance}
                        className={'px-4 py-2 rounded-lg text-white bg-warning-500 hover:bg-warning-600'}
                      >
                        Assign Compliance
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Update Answer Modal (No -> Yes) */}
            {showResponseModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gov-800">Update Answer Status</h3>
                    <button onClick={closeResponseModal} className="p-2 hover:bg-gov-100 rounded-md">
                      <X className="w-5 h-5 text-gov-600" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-gov-800">{responseModalItem?.question_text}</div>
                    {responseModalItem?.category && (
                      <div className="text-xs text-gov-600">Category: {responseModalItem?.category}</div>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm text-gov-700">Current status</span>
                      <span className="ml-2 px-2 py-0.5 text-xs rounded bg-red-500/20 border border-red-400/30 text-red-600">No</span>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-2">
                    <button onClick={closeResponseModal} className="px-3 py-2 text-sm rounded-md border border-gov-300 text-gov-700 hover:bg-gov-100">Cancel</button>
                    <button onClick={markItemCompliant} className="px-3 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700">Mark as Complete</button>
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Analytics Section */}
            {analyticsView === 'detailed' && (
              <motion.div variants={itemVariants} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <Activity className="w-6 h-6 text-cyber-400" />
                    <span>Security Metrics</span>
                  </h2>
                  <div className="flex items-center space-x-2 text-sm text-cyber-200/70 bg-cyber-500/20 px-3 py-1.5 rounded-lg border border-cyber-400/30">
                    <Activity className="w-4 h-4" />
                    <span>Real-time monitoring</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {Object.entries(analyticsData.securityMetrics).map(([key, metric]) => (
                    <div key={key} className="bg-white/5 border border-white/20 rounded-xl p-4 hover:bg-white/10 transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-cyber-200/80 capitalize">{key.replace(/([A-Z])/g, ' $1')}</h3>
                        {getTrendIcon(metric.trend)}
                      </div>
                      <div className="flex items-end space-x-2">
                        <span className="text-2xl font-bold text-white">{metric.current}</span>
                        <span className={`text-sm ${getTrendColor(metric.trend)}`}>
                          {metric.trend === 'up' ? '+' : metric.trend === 'down' ? '-' : ''}
                          {Math.abs(calculatePercentageChange(metric.current, metric.previous))}%
                        </span>
                      </div>
                      <div className="mt-2">
                        {renderMiniChart(analyticsData.monthlyTrends[key === 'vulnerabilities' ? 'incidents' : key] || [1,2,3,4,5], metric.trend === 'up' ? '#059669' : '#DC2626')}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Risk Distribution */}
                  <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                      <PieChart className="w-5 h-5 text-warning-400" />
                      <span>Risk Distribution</span>
                    </h3>
                    <div className="space-y-3">
                      {analyticsData.riskDistribution.map((risk) => (
                        <div key={risk.name} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-4 h-4 rounded-full shadow-lg" 
                              style={{ backgroundColor: risk.color, boxShadow: `0 0 8px ${risk.color}50` }}
                            />
                            <span className="text-sm font-medium text-white">{risk.name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-cyber-300">{risk.value}%</span>
                            <div className="w-16 bg-white/20 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full transition-all duration-500" 
                                style={{ 
                                  width: `${risk.value}%`, 
                                  backgroundColor: risk.color 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Compliance Frameworks */}
                  <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-success-400" />
                      <span>Compliance Frameworks</span>
                    </h3>
                    <div className="space-y-4">
                      {analyticsData.complianceBreakdown.map((framework) => (
                        <div key={framework.framework} className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-white">{framework.framework}</span>
                            <span className="text-sm text-cyber-300">{framework.score}% / {framework.target}%</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-white/20 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all duration-500 ${
                                  framework.score >= framework.target ? 'bg-success-400' : 
                                  framework.score >= framework.target * 0.8 ? 'bg-warning-400' : 'bg-red-400'
                                }`}
                                style={{ width: `${(framework.score / framework.target) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-cyber-200/70 bg-cyber-500/20 px-2 py-1 rounded">{framework.items} items</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Trend Analysis */}
            {analyticsView === 'trends' && (
              <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyber-900/50 to-cyber-800/30 backdrop-blur-sm border border-white/20 rounded-xl p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <TrendingUp className="w-6 h-6 text-cyber-400" />
                    <span>Trend Analysis</span>
                  </h2>
                  <div className="flex items-center space-x-2">
                    <select 
                      value={selectedMetric} 
                      onChange={(e) => setSelectedMetric(e.target.value)}
                      className="text-sm border border-white/20 rounded-lg px-3 py-1 bg-white/10 text-white focus:ring-2 focus:ring-cyber-400 focus:border-transparent backdrop-blur-sm"
                    >
                      <option value="industry" className="bg-cyber-800 text-white">vs Industry Average</option>
                      <option value="government" className="bg-cyber-800 text-white">vs Government Sector</option>
                      <option value="both" className="bg-cyber-800 text-white">vs Both</option>
                    </select>
                  </div>
                </div>
                
                <div className="bg-white/5 border border-white/20 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-white">{getMetricDisplayName(selectedMetric)} Trend</h3>
                    <div className="flex items-center space-x-4 text-sm text-cyber-200">
                      <div className="flex items-center space-x-1 bg-cyber-500/20 px-3 py-1 rounded-lg">
                        <TrendingUp className="w-4 h-4 text-cyber-400" />
                        <span>12-month view</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Large trend chart */}
                  <div className="h-64 flex items-end justify-between space-x-1 bg-white/5 rounded-lg p-4">
                    {analyticsData.monthlyTrends[selectedMetric].map((value, index) => {
                      const maxValue = Math.max(...analyticsData.monthlyTrends[selectedMetric])
                      const height = (value / maxValue) * 100
                      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                      
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center">
                          <div 
                            className="w-full bg-gradient-to-t from-cyber-500 to-cyber-400 rounded-t transition-all duration-500 hover:from-cyber-600 hover:to-cyber-500 shadow-lg"
                            style={{ height: `${height}%` }}
                            title={`${months[index]}: ${value}${selectedMetric !== 'incidents' ? '%' : ''}`}
                          />
                          <span className="text-xs text-cyber-200/70 mt-2">{months[index]}</span>
                        </div>
                      )
                    })}
                  </div>
                  
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center bg-white/5 rounded-lg p-4 border border-white/20">
                      <div className="text-2xl font-bold text-success-400">
                        {analyticsData.monthlyTrends[selectedMetric][analyticsData.monthlyTrends[selectedMetric].length - 1]}
                        {selectedMetric !== 'incidents' && '%'}
                      </div>
                      <div className="text-sm text-cyber-200/70">Current</div>
                    </div>
                    <div className="text-center bg-white/5 rounded-lg p-4 border border-white/20">
                      <div className="text-2xl font-bold text-cyber-400">
                        {(analyticsData.monthlyTrends[selectedMetric].reduce((a, b) => a + b, 0) / analyticsData.monthlyTrends[selectedMetric].length).toFixed(1)}
                        {selectedMetric !== 'incidents' && '%'}
                      </div>
                      <div className="text-sm text-cyber-200/70">Average</div>
                    </div>
                    <div className="text-center bg-white/5 rounded-lg p-4 border border-white/20">
                      <div className="text-2xl font-bold text-warning-400">
                        +{calculatePercentageChange(
                          analyticsData.monthlyTrends[selectedMetric][analyticsData.monthlyTrends[selectedMetric].length - 1],
                          analyticsData.monthlyTrends[selectedMetric][0]
                        )}%
                      </div>
                      <div className="text-sm text-cyber-200/70">YoY Growth</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Benchmarking Comparison */}
            {analyticsView === 'comparison' && (
              <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyber-900/50 to-cyber-800/30 backdrop-blur-sm border border-white/20 rounded-xl p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <BarChart3 className="w-6 h-6 text-cyber-400" />
                    <span>Industry Benchmarking</span>
                  </h2>
                  <div className="flex items-center space-x-2">
                    <select 
                      value={comparisonMode} 
                      onChange={(e) => setComparisonMode(e.target.value)}
                      className="text-sm border border-white/20 rounded-lg px-3 py-1 bg-white/10 text-white focus:ring-2 focus:ring-cyber-400 focus:border-transparent backdrop-blur-sm"
                    >
                      <option value="industry">vs Industry Average</option>
                      <option value="government">vs Government Sector</option>
                      <option value="both">vs Both</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(analyticsData.industryComparison).map(([metric, data]) => (
                    <div key={metric} className="bg-white/5 border border-white/20 rounded-xl p-4">
                      <h3 className="text-lg font-medium text-white mb-4">{getMetricDisplayName(metric)}</h3>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                          <span className="text-sm font-medium text-white">Your Organization</span>
                          <span className="text-lg font-bold text-cyber-400">
                            {data.yours}{metric === 'incidents' ? '' : '%'}
                          </span>
                        </div>
                        
                        {(comparisonMode === 'industry' || comparisonMode === 'both') && (
                          <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                            <span className="text-sm text-cyber-200">Industry Average</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-white">
                                {data.industry}{metric === 'incidents' ? '' : '%'}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                (metric === 'incidents' ? data.yours < data.industry : data.yours > data.industry) 
                                  ? 'bg-success-400/20 text-success-400' 
                                  : 'bg-red-400/20 text-red-400'
                              }`}>
                                {metric === 'incidents' 
                                  ? (data.yours < data.industry ? 'Better' : 'Worse')
                                  : (data.yours > data.industry ? 'Better' : 'Worse')
                                }
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {(comparisonMode === 'government' || comparisonMode === 'both') && (
                          <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                            <span className="text-sm text-cyber-200">Government Sector</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-white">
                                {data.government}{metric === 'incidents' ? '' : '%'}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                (metric === 'incidents' ? data.yours < data.government : data.yours > data.government) 
                                  ? 'bg-success-400/20 text-success-400' 
                                  : 'bg-red-400/20 text-red-400'
                              }`}>
                                {metric === 'incidents' 
                                  ? (data.yours < data.government ? 'Better' : 'Worse')
                                  : (data.yours > data.government ? 'Better' : 'Worse')
                                }
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Visual comparison bars */}
                        <div className="space-y-2 bg-white/5 rounded-lg p-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-16 text-xs text-cyber-200">You</div>
                            <div className="flex-1 bg-white/20 rounded-full h-2">
                              <div 
                                className="h-2 bg-cyber-400 rounded-full transition-all duration-500"
                                style={{ 
                                  width: `${metric === 'incidents' 
                                    ? Math.min((20 - data.yours) / 20 * 100, 100)
                                    : Math.min(data.yours, 100)
                                  }%` 
                                }}
                              />
                            </div>
                          </div>
                          
                          {(comparisonMode === 'industry' || comparisonMode === 'both') && (
                            <div className="flex items-center space-x-2">
                              <div className="w-16 text-xs text-cyber-200">Industry</div>
                              <div className="flex-1 bg-white/20 rounded-full h-2">
                                <div 
                                  className="h-2 bg-white/40 rounded-full transition-all duration-500"
                                  style={{ 
                                    width: `${metric === 'incidents' 
                                      ? Math.min((20 - data.industry) / 20 * 100, 100)
                                      : Math.min(data.industry, 100)
                                    }%` 
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          
                          {(comparisonMode === 'government' || comparisonMode === 'both') && (
                            <div className="flex items-center space-x-2">
                              <div className="w-16 text-xs text-cyber-200">Gov</div>
                              <div className="flex-1 bg-white/20 rounded-full h-2">
                                <div 
                                  className="h-2 bg-warning-400 rounded-full transition-all duration-500"
                                  style={{ 
                                    width: `${metric === 'incidents' 
                                      ? Math.min((20 - data.government) / 20 * 100, 100)
                                      : Math.min(data.government, 100)
                                    }%` 
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Recommendations - Only show for non-admin users */}
            {!(profile?.user_type === 'it_administrator' || profile?.user_type === 'security_officer' || profile?.role === 'Administrator') && dashboardData.recommendations.length > 0 && (
              <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyber-900/50 to-cyber-800/30 backdrop-blur-sm border border-white/20 rounded-xl p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <Target className="w-6 h-6 text-cyber-400" />
                    <span>Priority Recommendations</span>
                  </h2>
                  <Link to="/assessment" className="text-cyber-400 hover:text-cyber-300 text-sm font-medium bg-cyber-500/20 px-3 py-1 rounded-lg transition-colors">
                    View All
                  </Link>
                </div>
                <div className="space-y-4">
                  {dashboardData.recommendations.slice(0, 3).map((rec, index) => (
                    <div key={rec.id} className="flex items-start space-x-3 p-3 bg-white/5 border border-white/20 rounded-lg hover:bg-white/10 transition-colors">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        rec.priority === 'high' ? 'bg-red-400' :
                        rec.priority === 'medium' ? 'bg-warning-400' : 'bg-success-400'
                      }`} />
                      <div className="flex-1">
                        <h3 className="font-medium text-white mb-1">{rec.title}</h3>
                        <p className="text-sm text-cyber-200/80">{rec.description}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        rec.priority === 'high' ? 'bg-red-400/20 text-red-400 border border-red-400/30' :
                        rec.priority === 'medium' ? 'bg-warning-400/20 text-warning-400 border border-warning-400/30' : 
                        'bg-success-400/20 text-success-400 border border-success-400/30'
                      }`}>
                        {rec.priority}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Quick Stats */}
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyber-900/50 to-cyber-800/30 backdrop-blur-sm border border-white/20 rounded-xl p-6 shadow-2xl">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-cyber-400" />
                <span>Quick Stats</span>
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-success-400" />
                    <span className="text-sm text-cyber-200">Assessments</span>
                  </div>
                  <span className="font-semibold text-white">{dashboardData.assessmentHistory.length}</span>
                </div>
                <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4 text-warning-400" />
                    <span className="text-sm text-cyber-200">Open Issues</span>
                  </div>
                  <span className="font-semibold text-white">{dashboardData.recommendations.length}</span>
                </div>
                <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <Award className="w-4 h-4 text-cyber-400" />
                    <span className="text-sm text-cyber-200">Improvements</span>
                  </div>
                  <span className="font-semibold text-white">+15%</span>
                </div>
                <div className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-cyber-400" />
                    <span className="text-sm text-cyber-200">Last Update</span>
                  </div>
                  <span className="font-semibold text-white">Today</span>
                </div>
              </div>
            </motion.div>

            {/* Performance Metrics */}
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyber-900/50 to-cyber-800/30 backdrop-blur-sm border border-white/20 rounded-xl p-6 shadow-2xl">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-cyber-400" />
                <span>Performance Metrics</span>
              </h2>
              <div className="space-y-4">
                {Object.entries(analyticsData.performanceMetrics).map(([key, metric]) => (
                  <div key={key} className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-cyber-200 capitalize">
                        {key.replace(/([A-Z])/g, ' $1')}
                      </span>
                      <div className="flex items-center space-x-1">
                        {getTrendIcon(metric.trend)}
                        <span className="text-sm font-semibold text-white">
                          {metric.current}{metric.unit}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-white/10 rounded-full h-1.5">
                        <div 
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            metric.trend === 'up' ? 'bg-success-400' : 
                            metric.trend === 'down' && key === 'responseTime' ? 'bg-success-400' : 
                            metric.trend === 'down' ? 'bg-red-400' : 'bg-cyber-400'
                          }`}
                          style={{ 
                            width: `${key === 'responseTime' 
                              ? Math.min((10 - metric.current) / 10 * 100, 100)
                              : Math.min(metric.current, 100)
                            }%` 
                          }}
                        />
                      </div>
                      <span className={`text-xs ${getTrendColor(metric.trend)}`}>
                        {metric.trend === 'up' ? '+' : metric.trend === 'down' ? '-' : ''}
                        {Math.abs(calculatePercentageChange(metric.current, metric.previous))}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>



            {/* Upcoming Tasks */}
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyber-900/50 to-cyber-800/30 backdrop-blur-sm border border-white/20 rounded-xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-cyber-400" />
                  <span>Upcoming Tasks</span>
                </h2>
              </div>
              <div className="space-y-3">
                {upcomingTasks.length > 0 ? (
                  upcomingTasks.map((task) => (
                    <div key={task.id} className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                      <Calendar className="w-4 h-4 text-cyber-400 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-white">{String(task.title || '')
                          .replace(/\[AG:[^\]]+\]\s*/gi, '')
                          .replace(/\[AID:[^\]]+\]\s*/gi, '')
                          .replace(/\[Compliance\]\s*/gi, '')
                          .replace(/\s+/g, ' ')
                          .trim()}</h3>
                        <p className="text-xs text-cyber-200">
                          {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full border ml-2 ${getStatusColor(task.status || 'pending')}`}>
                        {formatStatusLabel(task.status || 'pending')}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <Calendar className="w-8 h-8 text-cyber-400 mx-auto mb-2" />
                    <p className="text-sm text-cyber-200">No upcoming tasks</p>
                    <p className="text-xs text-cyber-300">
                      <Link to="/task-reminders" className="text-cyber-400 hover:text-cyber-300">
                        Create your first task
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Recent Activity */}
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyber-900/50 to-cyber-800/30 backdrop-blur-sm border border-white/20 rounded-xl p-6 shadow-2xl">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-cyber-400" />
                <span>Recent Activity</span>
              </h2>
              <div className="space-y-3">
                {recentActivities.length > 0 ? (
                  recentActivities.slice(0, 5).map((activity, index) => (
                    <div key={activity.id || index} className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                      <div className="w-2 h-2 bg-cyber-400 rounded-full mt-2" />
                      <div className="flex-1">
                        <p className="text-sm text-white">{activity.action}</p>
                        <p className="text-xs text-cyber-200">
                          {activity.timestamp && activity.timestamp.includes('ago') 
                            ? activity.timestamp 
                            : formatActivityTime(activity.timestamp)
                          }
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <Activity className="w-8 h-8 text-cyber-400 mx-auto mb-2" />
                    <p className="text-sm text-cyber-200">No recent activities</p>
                    <p className="text-xs text-cyber-300">Activities will appear here as you use the system</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Assessment History Modal */}
      {showAssessmentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-cyber-900 to-cyber-800 border border-white/20 rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                <FileText className="w-6 h-6 text-cyber-400" />
                <span>Assessment History - {selectedAgencyForAssessments}</span>
              </h2>
              <button
                onClick={() => setShowAssessmentModal(false)}
                className="text-cyber-300 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {agencyAssessments.length > 0 ? (
              <div className="space-y-4">
                {agencyAssessments.map((assessment, index) => (
                  <div key={assessment.id || index} className="bg-white/5 border border-white/20 rounded-lg p-4 hover:bg-white/10 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-cyber-500/20 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-cyber-400" />
                        </div>
                        <div>
                          <h3 className="font-medium text-white">{assessment.title || 'Security Assessment'}</h3>
                          <p className="text-sm text-cyber-300">
                            {assessment.user_name || 'Unknown User'} • {new Date(assessment.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          assessment.score >= 80 ? 'bg-success-400/20 text-success-400 border border-success-400/30' :
                          assessment.score >= 60 ? 'bg-warning-400/20 text-warning-400 border border-warning-400/30' :
                          'bg-red-400/20 text-red-400 border border-red-400/30'
                        }`}>
                          Score: {assessment.score}%
                        </span>
                        <Link
                          to={`/assessment/${assessment.id}`}
                          className="px-3 py-1 text-xs bg-cyber-500/20 text-cyber-400 border border-cyber-500/30 rounded-full hover:bg-cyber-500/30 transition-colors"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                    
                    {assessment.description && (
                      <p className="text-sm text-cyber-200 mb-3">{assessment.description}</p>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-cyber-400">Risk Level:</span>
                        <span className={`ml-2 font-medium ${
                          assessment.risk_level === 'low' ? 'text-success-400' :
                          assessment.risk_level === 'medium' ? 'text-warning-400' :
                          'text-red-400'
                        }`}>
                          {assessment.risk_level?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </div>
                      <div>
                        <span className="text-cyber-400">Status:</span>
                        <span className={`ml-2 font-medium ${
                          assessment.status === 'completed' ? 'text-success-400' :
                          assessment.status === 'in_progress' ? 'text-warning-400' :
                          'text-cyber-400'
                        }`}>
                          {assessment.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-cyber-400 mx-auto mb-4" />
                <p className="text-cyber-200 mb-2">No assessments found for this agency</p>
                <p className="text-sm text-cyber-300">Users from this agency haven't completed any assessments yet.</p>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAssessmentModal(false)}
                className="px-4 py-2 bg-cyber-600 hover:bg-cyber-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
